

import { useState, useEffect } from "react";
import DOMPurify from "dompurify";
import Papa from "papaparse";
import { useNavigate } from "react-router-dom";
import notify from '../lib/notify';
import { supabase } from "../lib/supabaseClient";
import { subscribeToChanges } from '../lib/realtimeSubscriptions';
import SidebarNavbar from "../components/SidebarNavbar";
import BottomNavbar from "../components/BottomNavbar";
import TicketConversation from "../components/TicketConversation";
import TopGafferConfig from "../components/TopGafferConfig";
import { PONTOON_ENABLED } from "../lib/featureFlags";
import { Button as FcButton, Card as FcCard, Input as FcInput, Select as FcSelect, Table as FcTable, Textarea as FcTextarea } from '../ui';
import { Tabs as FcTabs } from '../ui';

export default function AdminFixtures({ session, userRole, username }) {
  // State for CSV upload
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvUploadMessage, setCsvUploadMessage] = useState("");
  const [csvFile, setCsvFile] = useState(null);

  // CSV file select handler
  const handleCsvFileChange = (e) => {
    setCsvFile(e.target.files[0] || null);
    setCsvUploadMessage("");
  };

  // Commit upload handler
  const handleCommitCsvUpload = async () => {
    if (!csvFile) return;
    setCsvUploading(true);
    setCsvUploadMessage("");
    Papa.parse(csvFile, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        const rows = results.data;
        let successCount = 0;
        let failCount = 0;
        for (const row of rows) {
          // Parse all relevant fields
          const upsertData = {
            id: row.id ? Number(row.id) : undefined,
            league: row.league,
            season: row.season,
            match_day: row.match_day ? Number(row.match_day) : undefined,
            home_team: row.home_team,
            away_team: row.away_team,
            kickoff_time: row.kickoff_time ? new Date(row.kickoff_time).toISOString() : null,
            status: row.status || "pending",
            home_score: row.home_score !== undefined && row.home_score !== "" ? Number(row.home_score) : null,
            away_score: row.away_score !== undefined && row.away_score !== "" ? Number(row.away_score) : null,
            result: row.result || null,
          };
          // Remove id if not present
          if (!upsertData.id) delete upsertData.id;
          // Remove home_score/away_score/result if not present
          if (upsertData.home_score === null) delete upsertData.home_score;
          if (upsertData.away_score === null) delete upsertData.away_score;
          if (!upsertData.result) delete upsertData.result;

          // Use composite key if no id
          let onConflict;
          if (upsertData.id !== undefined) {
            onConflict = "id";
          } else {
            onConflict = "league,season,match_day,home_team,away_team";
          }
          const { error } = await supabase.from("fixtures").upsert([upsertData], { onConflict });
          if (error) {
            failCount++;
          } else {
            successCount++;
          }
        }
        setCsvUploadMessage(`Upload complete: ${successCount} succeeded, ${failCount} failed.`);
        setCsvUploading(false);
        setCsvFile(null);
        if (selectedLeague && selectedSeason && currentMatchday) {
          fetchFixtures(selectedLeague, selectedSeason, currentMatchday);
        }
      },
      error: (err) => {
        setCsvUploadMessage("CSV parse error: " + err.message);
        setCsvUploading(false);
      },
    });
  };
  const [user, setUser] = useState(null);
  const [authorized, setAuthorized] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // New state for league/season selection and matchday navigation
  const [selectedLeague, setSelectedLeague] = useState("");
  const [selectedSeason, setSelectedSeason] = useState("");
  const [currentMatchday, setCurrentMatchday] = useState(null);
  const [maxMatchday, setMaxMatchday] = useState(38); // Default max, can be updated
  const [fixtures, setFixtures] = useState([]);
  const [editStates, setEditStates] = useState({});

  // State for new competition creation
  const [newCompetition, setNewCompetition] = useState({
    name: "",
    type: "",
    league: "",
    season: "",
    startDate: "",
    startMatchDay: 1,
    revival_strategy: "free_pass",
    is_18_plus: false,
    is_sponsored: false,
    sponsor_id: "",
    sponsor_matchday_count: 1,
    topGaffer: { enabled: false, stageCount: 1, stages: [] },
    pontoon: { enabled: false, matchDays: "" },
  });
  const [futureMatchdays, setFutureMatchdays] = useState([]);

  // State for release notes management
  const [releaseNotes, setReleaseNotes] = useState([]);
  const [showReleaseNotesForm, setShowReleaseNotesForm] = useState(false);
  const [editingReleaseNote, setEditingReleaseNote] = useState(null);
  const [newReleaseNote, setNewReleaseNote] = useState({
    version: "",
    title: "",
    content: "",
    release_date: new Date().toISOString().split('T')[0],
    is_published: false,
  });

  // State for sponsor management
  const [sponsors, setSponsors] = useState([]);
  const [showSponsorForm, setShowSponsorForm] = useState(false);
  const [editingSponsor, setEditingSponsor] = useState(null);
  const [newSponsor, setNewSponsor] = useState({
    name: "",
    logo_url: "",
    website_url: "",
    color_primary: "#284239",
    color_secondary: "#BA6830",
    active: true,
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [useUrlInput, setUseUrlInput] = useState(false);

  // State for sponsor question management
  const [sponsorQuestions, setSponsorQuestions] = useState([]);
  const [selectedExportCompetition, setSelectedExportCompetition] = useState('');
  const [exportingData, setExportingData] = useState(false);
  const [exportMessage, setExportMessage] = useState(null);
  const [selectedCompetitionForQuestions, setSelectedCompetitionForQuestions] = useState("");
  const [sponsoredCompetitions, setSponsoredCompetitions] = useState([]);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [newQuestion, setNewQuestion] = useState({
    type: "short_text",
    label: "",
    options: [],
    required: true,
    sort_order: 0,
  });
  const [optionInput, setOptionInput] = useState("");

  // State for support tickets management
  const [supportTickets, setSupportTickets] = useState([]);
  const [ticketFilter, setTicketFilter] = useState("all");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [adminNotes, setAdminNotes] = useState("");
  const [updatingTicket, setUpdatingTicket] = useState(false);

  // State for legal documents management
  const [legalDocuments, setLegalDocuments] = useState([]);
  const [showLegalDocForm, setShowLegalDocForm] = useState(false);
  const [editingLegalDoc, setEditingLegalDoc] = useState(null);
  const [newLegalDoc, setNewLegalDoc] = useState({
    type: "terms",
    version: "",
    title: "",
    content: "",
    effective_date: new Date().toISOString().split('T')[0],
    is_active: false,
  });
  const [legalDocFilter, setLegalDocFilter] = useState("all"); // 'all', 'terms', 'privacy', 'cookies'
  const [activeAdminTab, setActiveAdminTab] = useState("competition");

  // Users tab state
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersSearch, setUsersSearch] = useState("");
  const [usersSummary, setUsersSummary] = useState({
    authSignups: 0,
    totalUsers: 0,
    newUsersInRange: 0,
    adminUsers: 0,
    missingProfiles: 0,
  });
  const [usersList, setUsersList] = useState([]);
  const [usersByTimeZone, setUsersByTimeZone] = useState([]);

  // Analytics tab state
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState("");
  const [analyticsLastUpdated, setAnalyticsLastUpdated] = useState("");
  const [analyticsDataSource, setAnalyticsDataSource] = useState("raw_tables");
  const [selectedAnalyticsCompetitionId, setSelectedAnalyticsCompetitionId] = useState("");
  const [analyticsRangePreset, setAnalyticsRangePreset] = useState("last_30_days");
  const [analyticsDateRange, setAnalyticsDateRange] = useState({
    startDate: "",
    endDate: "",
  });
  const [analyticsData, setAnalyticsData] = useState({
    weeklyActiveSelectors: 0,
    totalSelections: 0,
    uniqueSelectors: 0,
    avgSelectionsPerSelector: 0,
    activeUsersDAU: 0,
    activeUsersWAU: 0,
    activeUsersMAU: 0,
    totalUsers: 0,
    newUsers: 0,
    totalCompetitions: 0,
    activeCompetitions: 0,
    completedCompetitionsInRange: 0,
    returningSelectors4Week: 0,
    firstTimeSelectors: 0,
    dormantUsers30Day: 0,
    sponsoredEntries: 0,
    sponsorQuestionnaireCompletionRate: 0,
    weeklyTrend: [],
    matchDayRows: [],
    leagueRows: [],
    competitionCurrentWeekRows: [],
  });

  useEffect(() => {
    const checkAuth = async () => {
      const { data, error } = await supabase.auth.getUser();
      if (error || !data?.user) {
        navigate("/login");
        return;
      }
      const { data: profile, error: roleError } = await supabase
        .from("profiles")
        .select("role")
        .eq("id", data.user.id)
        .single();

      if (roleError || profile?.role !== "admin") {
        notify("Access denied. Admins only.", 'error');
        navigate("/");
      } else {
        setUser(data.user);
        setAuthorized(true);
      }
      setLoading(false);
    };
    checkAuth();
  }, [navigate]);

  // Fetch earliest incomplete matchday when league/season changes
  useEffect(() => {
    if (selectedLeague && selectedSeason) {
      fetchEarliestIncompleteMatchday(selectedLeague, selectedSeason);
    } else {
      setFixtures([]);
      setCurrentMatchday(null);
    }
  }, [selectedLeague, selectedSeason]);

  // Fetch fixtures when matchday changes
  useEffect(() => {
    if (selectedLeague && selectedSeason && currentMatchday) {
      fetchFixtures(selectedLeague, selectedSeason, currentMatchday);
    }
  }, [currentMatchday]);

  async function fetchEarliestIncompleteMatchday(league, season) {
    // Find the earliest matchday with at least one fixture not complete
    const { data, error } = await supabase
      .from("fixtures")
      .select("match_day, status")
      .eq("league", league)
      .eq("season", season)
      .order("match_day", { ascending: true });
    if (error) {
      setCurrentMatchday(1);
      return;
    }
    if (!data || data.length === 0) {
      setCurrentMatchday(1);
      return;
    }
    // Find the lowest matchday with any fixture not complete
    let earliest = null;
    let max = 1;
    data.forEach((f) => {
      if (!earliest && f.status !== "complete") earliest = f.match_day;
      if (f.match_day > max) max = f.match_day;
    });
    setMaxMatchday(max);
    setCurrentMatchday(earliest || 1);
  }

  async function fetchFixtures(league, season, matchday) {
    if (!league || !season || !matchday) return;
    const { data, error } = await supabase
      .from("fixtures")
      .select("*")
      .eq("league", league)
      .eq("season", season)
      .eq("match_day", parseInt(matchday))
      .order("kickoff_time", { ascending: true });
    if (error) {
      notify('Failed to fetch fixtures', 'error');
    } else {
      setFixtures(data || []);
      const initialEditStates = {};
      (data || []).forEach((f) => (initialEditStates[f.id] = false));
      setEditStates(initialEditStates);
    }
  }

  async function updateFixture(fixtureId, homeScore, awayScore) {
  // TODO: replace this with a non-blocking modal confirm (admin UX)
    const result =
      homeScore > awayScore
        ? "home_win"
        : homeScore < awayScore
        ? "away_win"
        : "draw";
    const status =
      homeScore !== null &&
      awayScore !== null &&
      homeScore !== undefined &&
      awayScore !== undefined
        ? "complete"
        : null;
    const updateData = {
      home_score: homeScore,
      away_score: awayScore,
      result,
      ...(status ? { status } : {}),
    };
    const { error: fixtureError } = await supabase
      .from("fixtures")
      .update(updateData)
      .eq("id", fixtureId);
    if (fixtureError) {
      notify('Failed to update fixture', 'error');
      return;
    }
    // Re-fetch fixtures for this matchday
    await fetchFixtures(selectedLeague, selectedSeason, currentMatchday);
  }

  // Fetch future matchdays when league/season changes in competition form
  useEffect(() => {
    const fetchFutureMatchdays = async () => {
      if (newCompetition.type === 'world_stage') {
        setFutureMatchdays([]);
        return;
      }

      if (!newCompetition.league || !newCompetition.season) return;
      
      const { data } = await supabase
        .from('matchdays_view')
        .select('match_day, first_kickoff_time')
        .eq('league', newCompetition.league)
        .eq('season', newCompetition.season)
        .gt('first_kickoff_time', new Date().toISOString())
        .order('match_day', { ascending: true });
      
      if (data && data.length > 0) {
        setFutureMatchdays(data);
        // Auto-set to first available matchday if not already set or if set to default 1
        if (newCompetition.startMatchDay === 1) {
          setNewCompetition(prev => ({ ...prev, startMatchDay: data[0].match_day }));
        }
      } else {
        setFutureMatchdays([]);
      }
    };
    
    fetchFutureMatchdays();
  }, [newCompetition.league, newCompetition.season, newCompetition.type]);

  async function createCompetition() {
    const { name, type, league, season, startDate, startMatchDay, is_18_plus, is_sponsored, sponsor_id, sponsor_matchday_count } = newCompetition;
    const requiresMatchday = type !== 'world_stage';
    const startMatchdayValue = requiresMatchday ? startMatchDay : 1;

    // Validate required fields
    if (!name || !type || !league || !season || !startDate || (requiresMatchday && !startMatchdayValue)) {
      notify("Please fill in all required fields.", 'info');
      return;
    }
    // Validate sponsor selection if sponsored
    if (is_sponsored && !sponsor_id) {
      notify("Please select a sponsor for this sponsored competition.", 'error');
      return;
    }
    // Validate startDate is today or future
    const today = new Date();
    const compDate = new Date(startDate);
    if (isNaN(compDate.getTime()) || compDate < today.setHours(0,0,0,0)) {
      notify("Start date must be today or a future date.", 'info');
      return;
    }
    // Pontoon (free side-game on a Knockout) — admin/public comps can enable it too.
    const pontoonCfg = newCompetition.pontoon || { enabled: false, matchDays: "" };
    const addPontoon = PONTOON_ENABLED && type === 'knockout' && pontoonCfg.enabled;
    let pontoonMatchDaysInt = null;
    if (addPontoon) {
      pontoonMatchDaysInt = parseInt(String(pontoonCfg.matchDays || '').replace(/\D/g, ''), 10);
      if (!pontoonMatchDaysInt || pontoonMatchDaysInt < 1) {
        notify('Pontoon match days must be a positive whole number.', 'info');
        return;
      }
    }

    // Top Gaffer client-side validation (the RPC re-validates authoritatively).
    const tg = newCompetition.topGaffer || { enabled: false };
    const tgEnabled = type === 'season' && tg.enabled;
    if (tgEnabled) {
      const available = futureMatchdays
        .map((m) => m.match_day)
        .filter((md) => md >= startMatchdayValue);
      const stages = tg.stages || [];
      const allocated = new Set(stages.flat());
      if (stages.length < 1 || stages.some((s) => !s || s.length === 0)) {
        notify("Each Top Gaffer stage must contain at least one matchday.", 'info');
        return;
      }
      if (allocated.size !== available.length || available.some((md) => !allocated.has(md))) {
        notify("Assign every matchday to a Top Gaffer stage before creating the competition.", 'info');
        return;
      }
    }

    const { data: created, error } = await supabase.from("competitions").insert([
      {
        name,
        type,
        league,
        season,
        registration_status: "open",
        start_date: startDate,
        start_match_day: startMatchdayValue,
        current_match_day: startMatchdayValue,
        created_by: session?.user?.id,
        created_at: new Date().toISOString(),
        is_18_plus,
        is_sponsored: is_sponsored,
        sponsor_id: is_sponsored ? sponsor_id : null,
        ...(type === 'knockout' ? { revival_strategy: newCompetition.revival_strategy } : {}),
      },
    ]).select();
    if (error || !created || !created[0]) {
      notify("Failed to create competition", 'error');
      return;
    }

    // Pontoon: create the linked child competition (type='pontoon'). Not publicly
    // joinable (registration closed) — entrants are auto-enrolled on elimination.
    if (addPontoon) {
      const { error: pontoonError } = await supabase.from('competitions').insert([
        {
          name: `${name} — Pontoon`,
          type: 'pontoon',
          league,
          season,
          revival_strategy: 'elimination',
          start_date: startDate,
          start_match_day: startMatchdayValue,
          current_match_day: startMatchdayValue,
          created_by: session?.user?.id,
          created_at: new Date().toISOString(),
          is_18_plus,
          registration_status: 'closed',
          parent_competition_id: created[0].id,
          pontoon_match_days: pontoonMatchDaysInt,
        },
      ]);
      if (pontoonError) {
        notify(`Competition created, but Pontoon setup failed: ${pontoonError.message}`, 'error');
      }
    }

    // Save Top Gaffer config via the validated RPC (competition must exist first).
    if (tgEnabled) {
      const p_stages = (tg.stages || []).map((mds, i) => ({
        stage_number: i + 1,
        matchdays: mds,
      }));
      const { error: tgError } = await supabase.rpc('save_top_gaffer_config', {
        p_competition_id: created[0].id,
        p_enabled: true,
        p_stages,
      });
      if (tgError) {
        notify(`Competition created, but Top Gaffer setup failed: ${tgError.message}`, 'error');
      }
    }

    notify("Competition created successfully!", 'success');
    setNewCompetition({
      name: "",
      type: "",
      league: "",
      season: "",
      startDate: "",
      startMatchDay: 1,
      revival_strategy: "free_pass",
      is_18_plus: false,
      is_sponsored: false,
      sponsor_id: "",
      sponsor_matchday_count: 1,
      topGaffer: { enabled: false, stageCount: 1, stages: [] },
      pontoon: { enabled: false, matchDays: "" },
    });
    // Refresh sponsored competitions list
    fetchSponsoredCompetitions();
  }

  // Release Notes Functions
  useEffect(() => {
    fetchReleaseNotes();
  }, []);

  const fetchReleaseNotes = async () => {
    try {
      const { data, error } = await supabase
        .from('release_notes')
        .select('*')
        .order('release_date', { ascending: false });
      
      if (error) {
        notify('Failed to fetch release notes', 'error');
      } else {
        setReleaseNotes(data || []);
      }
    } catch (err) {
      notify('Failed to fetch release notes', 'error');
    }
  };

  const handleCreateReleaseNote = async () => {
    if (!newReleaseNote.version || !newReleaseNote.title || !newReleaseNote.content) {
      notify('Please fill in all required fields', 'error');
      return;
    }

    try {
      const { error } = await supabase
        .from('release_notes')
        .insert([{
          ...newReleaseNote,
          created_by: user.id,
        }]);

      if (error) {
        notify('Failed to create release note', 'error');
      } else {
        notify('Release note created successfully!', 'success');
        setNewReleaseNote({
          version: "",
          title: "",
          content: "",
          release_date: new Date().toISOString().split('T')[0],
          is_published: false,
        });
        setShowReleaseNotesForm(false);
        fetchReleaseNotes();
      }
    } catch (err) {
      notify('Failed to create release note', 'error');
    }
  };

  const handleUpdateReleaseNote = async () => {
    if (!editingReleaseNote.version || !editingReleaseNote.title || !editingReleaseNote.content) {
      notify('Please fill in all required fields', 'error');
      return;
    }

    try {
      const { error } = await supabase
        .from('release_notes')
        .update({
          version: editingReleaseNote.version,
          title: editingReleaseNote.title,
          content: editingReleaseNote.content,
          release_date: editingReleaseNote.release_date,
          is_published: editingReleaseNote.is_published,
        })
        .eq('id', editingReleaseNote.id);

      if (error) {
        notify('Failed to update release note', 'error');
      } else {
        notify('Release note updated successfully!', 'success');
        setEditingReleaseNote(null);
        fetchReleaseNotes();
      }
    } catch (err) {
      notify('Failed to update release note', 'error');
    }
  };

  const handleDeleteReleaseNote = async (id) => {
    if (!confirm('Are you sure you want to delete this release note?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('release_notes')
        .delete()
        .eq('id', id);

      if (error) {
        notify('Failed to delete release note', 'error');
      } else {
        notify('Release note deleted successfully!', 'success');
        fetchReleaseNotes();
      }
    } catch (err) {
      notify('Failed to delete release note', 'error');
    }
  };

  const togglePublishStatus = async (id, currentStatus) => {
    try {
      const { error } = await supabase
        .from('release_notes')
        .update({ is_published: !currentStatus })
        .eq('id', id);

      if (error) {
        notify('Failed to update publish status', 'error');
      } else {
        notify(`Release note ${!currentStatus ? 'published' : 'unpublished'}!`, 'success');
        fetchReleaseNotes();
      }
    } catch (err) {
      notify('Failed to update publish status', 'error');
    }
  };

  // Sponsor Management Functions
  useEffect(() => {
    fetchSponsors();
  }, []);

  const fetchSponsors = async () => {
    try {
      const { data, error } = await supabase
        .from('sponsors')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        notify('Failed to fetch sponsors', 'error');
      } else {
        setSponsors(data || []);
      }
    } catch (err) {
      notify('Failed to fetch sponsors', 'error');
    }
  };

  // Logo upload helper functions
  const validateLogoFile = (file) => {
    const maxSize = 2 * 1024 * 1024; // 2MB
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/svg+xml'];
    
    if (!allowedTypes.includes(file.type)) {
      notify('Invalid file type. Please upload PNG, JPG, WebP, or SVG.', 'error');
      return false;
    }
    
    if (file.size > maxSize) {
      notify('File too large. Maximum size is 2MB.', 'error');
      return false;
    }
    
    return true;
  };

  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      
      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        const { width, height } = img;
        
        // Check minimum dimensions
        if (width < 200 || height < 200) {
          notify('Image too small. Minimum dimensions: 200x200px.', 'error');
          resolve(null);
          return;
        }
        
        // If image is within limits, return original file
        if (width <= 1000 && height <= 1000) {
          resolve(file);
          return;
        }
        
        // Calculate new dimensions maintaining aspect ratio
        let newWidth = width;
        let newHeight = height;
        
        if (width > height) {
          newWidth = 1000;
          newHeight = Math.round((height / width) * 1000);
        } else {
          newHeight = 1000;
          newWidth = Math.round((width / height) * 1000);
        }
        
        // Create canvas and compress
        const canvas = document.createElement('canvas');
        canvas.width = newWidth;
        canvas.height = newHeight;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, newWidth, newHeight);
        
        // Convert to blob with quality compression
        canvas.toBlob((blob) => {
          if (!blob) {
            notify('Failed to compress image.', 'error');
            resolve(null);
            return;
          }
          
          // Create new file from blob
          const compressedFile = new File([blob], file.name, {
            type: file.type,
            lastModified: Date.now(),
          });
          
          notify(`Image compressed from ${width}x${height} to ${newWidth}x${newHeight}`, 'success');
          resolve(compressedFile);
        }, file.type, 0.9); // 90% quality
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        notify('Failed to load image for compression.', 'error');
        resolve(null);
      };
      
      img.src = objectUrl;
    });
  };

  const handleLogoFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!validateLogoFile(file)) {
      e.target.value = '';
      return;
    }
    
    // For SVG, skip compression
    if (file.type === 'image/svg+xml') {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
      return;
    }
    
    // Compress/validate image
    const processedFile = await compressImage(file);
    if (processedFile) {
      setLogoFile(processedFile);
      setLogoPreview(URL.createObjectURL(processedFile));
    } else {
      e.target.value = '';
    }
  };

  const uploadLogoToStorage = async (file, sponsorName) => {
    try {
      setUploadingLogo(true);
      
      const fileExt = file.name.split('.').pop();
      const fileName = `logos/${sponsorName.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}.${fileExt}`;
      
      const { data, error } = await supabase.storage
        .from('sponsors')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        notify('Failed to upload logo to storage', 'error');
        return null;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('sponsors')
        .getPublicUrl(fileName);

      setUploadingLogo(false);
      return publicUrl;
    } catch (err) {
      notify('Failed to upload logo', 'error');
      setUploadingLogo(false);
      return null;
    }
  };

  const handleCreateSponsor = async () => {
    if (!newSponsor.name) {
      notify('Sponsor name is required', 'error');
      return;
    }

    try {
      let logoUrl = newSponsor.logo_url;

      // Upload file if provided
      if (logoFile) {
        logoUrl = await uploadLogoToStorage(logoFile, newSponsor.name);
        if (!logoUrl) return; // Upload failed
      }

      const { error } = await supabase
        .from('sponsors')
        .insert([{ ...newSponsor, logo_url: logoUrl }]);

      if (error) {
        notify('Failed to create sponsor', 'error');
      } else {
        notify('Sponsor created successfully!', 'success');
        setNewSponsor({
          name: "",
          logo_url: "",
          website_url: "",
          color_primary: "#284239",
          color_secondary: "#BA6830",
          active: true,
        });
        setLogoFile(null);
        setLogoPreview("");
        setShowSponsorForm(false);
        setUseUrlInput(false);
        fetchSponsors();
      }
    } catch (err) {
      notify('Failed to create sponsor', 'error');
    }
  };

  const handleUpdateSponsor = async () => {
    if (!editingSponsor.name) {
      notify('Sponsor name is required', 'error');
      return;
    }

    try {
      let logoUrl = editingSponsor.logo_url;

      // Upload new file if provided
      if (logoFile) {
        logoUrl = await uploadLogoToStorage(logoFile, editingSponsor.name);
        if (!logoUrl) return; // Upload failed
      }

      const { error } = await supabase
        .from('sponsors')
        .update({
          name: editingSponsor.name,
          logo_url: logoUrl,
          website_url: editingSponsor.website_url,
          color_primary: editingSponsor.color_primary,
          color_secondary: editingSponsor.color_secondary,
          active: editingSponsor.active,
        })
        .eq('id', editingSponsor.id);

      if (error) {
        notify('Failed to update sponsor', 'error');
      } else {
        notify('Sponsor updated successfully!', 'success');
        setEditingSponsor(null);
        setLogoFile(null);
        setLogoPreview("");
        setUseUrlInput(false);
        fetchSponsors();
      }
    } catch (err) {
      notify('Failed to update sponsor', 'error');
    }
  };

  const handleDeleteSponsor = async (id) => {
    if (!confirm('Are you sure you want to delete this sponsor? This will remove the sponsor link from all competitions.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('sponsors')
        .delete()
        .eq('id', id);

      if (error) {
        notify('Failed to delete sponsor', 'error');
      } else {
        notify('Sponsor deleted successfully!', 'success');
        fetchSponsors();
      }
    } catch (err) {
      notify('Failed to delete sponsor', 'error');
    }
  };

  const toggleSponsorStatus = async (id, currentStatus) => {
    try {
      const { error} = await supabase
        .from('sponsors')
        .update({ active: !currentStatus })
        .eq('id', id);

      if (error) {
        notify('Failed to update sponsor status', 'error');
      } else {
        notify(`Sponsor ${!currentStatus ? 'activated' : 'deactivated'}!`, 'success');
        fetchSponsors();
      }
    } catch (err) {
      notify('Failed to update sponsor status', 'error');
    }
  };

  // Sponsor Question Management Functions
  useEffect(() => {
    fetchSponsoredCompetitions();
  }, []);

  useEffect(() => {
    if (selectedCompetitionForQuestions) {
      fetchSponsorQuestions(selectedCompetitionForQuestions);
    }
  }, [selectedCompetitionForQuestions]);

  const fetchSponsoredCompetitions = async () => {
    try {
      const { data, error } = await supabase
        .from('competitions')
        .select('id, name, league, season, sponsor_id, sponsors(name)')
        .eq('is_sponsored', true)
        .eq('type', 'scout')
        .order('created_at', { ascending: false });
      
      if (error) {
        notify('Failed to fetch sponsored competitions', 'error');
      } else {
        setSponsoredCompetitions(data || []);
      }
    } catch (err) {
      notify('Failed to fetch sponsored competitions', 'error');
    }
  };

  const fetchSponsorQuestions = async (competitionId) => {
    try {
      const { data, error } = await supabase
        .from('sponsor_questions')
        .select('*')
        .eq('competition_id', competitionId)
        .order('sort_order', { ascending: true });
      
      if (error) {
        notify('Failed to fetch questions', 'error');
      } else {
        setSponsorQuestions(data || []);
      }
    } catch (err) {
      notify('Failed to fetch questions', 'error');
    }
  };

  const handleCreateQuestion = async () => {
    if (!selectedCompetitionForQuestions) {
      notify('Please select a competition first', 'error');
      return;
    }

    if (!newQuestion.label.trim()) {
      notify('Question label is required', 'error');
      return;
    }

    // Check max 3 questions limit
    if (sponsorQuestions.length >= 3) {
      notify('Maximum 3 questions per competition allowed', 'error');
      return;
    }

    // Validate options for MCQ types
    const mcqTypes = ['radio', 'dropdown', 'multi'];
    if (mcqTypes.includes(newQuestion.type) && newQuestion.options.length === 0) {
      notify('Please add at least one option for this question type', 'error');
      return;
    }

    try {
      const { error } = await supabase
        .from('sponsor_questions')
        .insert([{
          competition_id: selectedCompetitionForQuestions,
          type: newQuestion.type,
          label: newQuestion.label,
          options: mcqTypes.includes(newQuestion.type) ? newQuestion.options : null,
          required: newQuestion.required,
          sort_order: sponsorQuestions.length,
        }]);

      if (error) {
        notify('Failed to create question', 'error');
      } else {
        notify('Question created successfully!', 'success');
        setNewQuestion({
          type: "short_text",
          label: "",
          options: [],
          required: true,
          sort_order: 0,
        });
        setShowQuestionForm(false);
        fetchSponsorQuestions(selectedCompetitionForQuestions);
      }
    } catch (err) {
      notify('Failed to create question', 'error');
    }
  };

  const handleUpdateQuestion = async () => {
    if (!editingQuestion.label.trim()) {
      notify('Question label is required', 'error');
      return;
    }

    const mcqTypes = ['radio', 'dropdown', 'multi'];
    if (mcqTypes.includes(editingQuestion.type) && editingQuestion.options.length === 0) {
      notify('Please add at least one option for this question type', 'error');
      return;
    }

    try {
      const { error } = await supabase
        .from('sponsor_questions')
        .update({
          type: editingQuestion.type,
          label: editingQuestion.label,
          options: mcqTypes.includes(editingQuestion.type) ? editingQuestion.options : null,
          required: editingQuestion.required,
        })
        .eq('id', editingQuestion.id);

      if (error) {
        notify('Failed to update question', 'error');
      } else {
        notify('Question updated successfully!', 'success');
        setEditingQuestion(null);
        fetchSponsorQuestions(selectedCompetitionForQuestions);
      }
    } catch (err) {
      notify('Failed to update question', 'error');
    }
  };

  const handleDeleteQuestion = async (id) => {
    if (!confirm('Are you sure you want to delete this question? All user answers will also be deleted.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('sponsor_questions')
        .delete()
        .eq('id', id);

      if (error) {
        notify('Failed to delete question', 'error');
      } else {
        notify('Question deleted successfully!', 'success');
        fetchSponsorQuestions(selectedCompetitionForQuestions);
      }
    } catch (err) {
      notify('Failed to delete question', 'error');
    }
  };

  // Support Tickets Management Functions
  useEffect(() => {
    if (authorized) {
      fetchSupportTickets();
      
      // Real-time subscription for ticket updates
      const unsubscribe = subscribeToChanges(supabase, 'support_tickets_admin',
          {
            event: '*',
            schema: 'public',
            table: 'support_tickets'
          },
          () => {
            fetchSupportTickets();
          }
        );

      return () => {
        unsubscribe();
      };
    }
  }, [authorized]);

  const fetchSupportTickets = async () => {
    try {
      const { data, error } = await supabase
        .from('support_tickets')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSupportTickets(data || []);
    } catch (error) {
      notify('Failed to load support tickets', 'error');
    }
  };

  const handleUpdateTicketStatus = async (ticketId, newStatus) => {
    setUpdatingTicket(true);
    try {
      const updateData = {
        status: newStatus,
        updated_at: new Date().toISOString()
      };

      // If resolving, set resolved_at timestamp
      if (newStatus === 'resolved' || newStatus === 'closed') {
        updateData.resolved_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from('support_tickets')
        .update(updateData)
        .eq('id', ticketId);

      if (error) throw error;

      notify('Ticket status updated successfully', 'success');
      fetchSupportTickets();
      setSelectedTicket(null);
    } catch (error) {
      notify('Failed to update ticket status', 'error');
    } finally {
      setUpdatingTicket(false);
    }
  };

  const handleUpdateAdminNotes = async (ticketId) => {
    setUpdatingTicket(true);
    try {
      const { error } = await supabase
        .from('support_tickets')
        .update({
          admin_notes: adminNotes,
          updated_at: new Date().toISOString()
        })
        .eq('id', ticketId);

      if (error) throw error;

      notify('Admin notes updated successfully', 'success');
      fetchSupportTickets();
      setAdminNotes('');
      setSelectedTicket(null);
    } catch (error) {
      notify('Failed to update admin notes', 'error');
    } finally {
      setUpdatingTicket(false);
    }
  };

  // Legal Documents Functions
  useEffect(() => {
    fetchLegalDocuments();
  }, []);

  const fetchLegalDocuments = async () => {
    try {
      const { data, error } = await supabase
        .from('legal_documents')
        .select('*')
        .order('effective_date', { ascending: false });
      
      if (error) {
        notify('Failed to fetch legal documents', 'error');
      } else {
        setLegalDocuments(data || []);
      }
    } catch (err) {
      notify('Failed to fetch legal documents', 'error');
    }
  };

  const handleCreateLegalDoc = async () => {
    if (!newLegalDoc.type || !newLegalDoc.version || !newLegalDoc.title || !newLegalDoc.content || !newLegalDoc.effective_date) {
      notify('Please fill in all required fields', 'error');
      return;
    }

    try {
      const { error } = await supabase
        .from('legal_documents')
        .insert([newLegalDoc]);

      if (error) {
        notify('Failed to create legal document', 'error');
      } else {
        notify('Legal document created successfully!', 'success');
        setNewLegalDoc({
          type: "terms",
          version: "",
          title: "",
          content: "",
          effective_date: new Date().toISOString().split('T')[0],
          is_active: false,
        });
        setShowLegalDocForm(false);
        fetchLegalDocuments();
      }
    } catch (err) {
      notify('Failed to create legal document', 'error');
    }
  };

  const handleUpdateLegalDoc = async () => {
    if (!editingLegalDoc.type || !editingLegalDoc.version || !editingLegalDoc.title || !editingLegalDoc.content || !editingLegalDoc.effective_date) {
      notify('Please fill in all required fields', 'error');
      return;
    }

    try {
      const { error } = await supabase
        .from('legal_documents')
        .update({
          type: editingLegalDoc.type,
          version: editingLegalDoc.version,
          title: editingLegalDoc.title,
          content: editingLegalDoc.content,
          effective_date: editingLegalDoc.effective_date,
          is_active: editingLegalDoc.is_active,
        })
        .eq('id', editingLegalDoc.id);

      if (error) {
        notify('Failed to update legal document', 'error');
      } else {
        notify('Legal document updated successfully!', 'success');
        setEditingLegalDoc(null);
        fetchLegalDocuments();
      }
    } catch (err) {
      notify('Failed to update legal document', 'error');
    }
  };

  const handleDeleteLegalDoc = async (docId) => {
    if (!confirm('Are you sure you want to delete this legal document? This cannot be undone.')) return;

    try {
      const { error } = await supabase
        .from('legal_documents')
        .delete()
        .eq('id', docId);

      if (error) {
        notify('Failed to delete legal document', 'error');
      } else {
        notify('Legal document deleted successfully', 'success');
        fetchLegalDocuments();
      }
    } catch (err) {
      notify('Failed to delete legal document', 'error');
    }
  };

  const toggleLegalDocActiveStatus = async (docId, currentStatus) => {
    try {
      const { error } = await supabase
        .from('legal_documents')
        .update({ is_active: !currentStatus })
        .eq('id', docId);

      if (error) {
        notify('Failed to update status', 'error');
      } else {
        notify(`Legal document ${!currentStatus ? 'activated' : 'deactivated'} successfully`, 'success');
        fetchLegalDocuments();
      }
    } catch (err) {
      notify('Failed to update status', 'error');
    }
  };

  // Competition CRUD
  const handleExportSponsorData = async () => {
    if (!selectedExportCompetition) return;
    
    try {
      setExportingData(true);
      setExportMessage(null);
      
      // Fetch aggregated responses from the view (one row per user)
      const { data: responses, error: responsesError } = await supabase
        .from('sponsor_responses_export')
        .select('*')
        .eq('competition_id', selectedExportCompetition);
      
      if (responsesError) throw responsesError;
      
      if (!responses || responses.length === 0) {
        setExportMessage({ type: 'error', text: 'No data found for this competition' });
        return;
      }
      
      // Escape commas and quotes in CSV
      const escapeCSV = (str) => {
        const stringVal = String(str || '');
        if (stringVal.includes(',') || stringVal.includes('"') || stringVal.includes('\n')) {
          return `"${stringVal.replace(/"/g, '""')}"`;
        }
        return stringVal;
      };
      
      // Get all unique question labels (for header row)
      const questionLabels = Object.keys(responses[0]?.answers || {});
      
      // Build CSV rows
      const csvRows = [];
      
      // Header row: Email, Username, Competition, League, Season, Sponsor, Question 1, Answer 1, Question 2, Answer 2, ..., First Submitted, Last Submitted
      const headerRow = [
        'Email',
        'Username',
        'Competition',
        'League',
        'Season',
        'Sponsor'
      ];
      
      // Add Question/Answer pairs to header
      questionLabels.forEach((_, index) => {
        headerRow.push(`Question ${index + 1}`);
        headerRow.push(`Answer ${index + 1}`);
      });
      
      headerRow.push('First Submitted', 'Last Submitted');
      csvRows.push(headerRow.map(escapeCSV).join(','));
      
      // Data rows: one per user
      responses.forEach(response => {
        const rowData = [
          response.email,
          response.username,
          response.competition_name,
          response.league,
          response.season,
          response.sponsor_name
        ];
        
        // Add each question and its answer as separate columns
        questionLabels.forEach(questionLabel => {
          const answer = response.answers[questionLabel];
          
          // Add question label
          rowData.push(questionLabel);
          
          // Format and add answer
          let formattedAnswer = '';
          if (Array.isArray(answer)) {
            formattedAnswer = answer.join('; ');
          } else if (typeof answer === 'object' && answer !== null) {
            formattedAnswer = JSON.stringify(answer);
          } else {
            formattedAnswer = String(answer || '');
          }
          
          rowData.push(formattedAnswer);
        });
        
        // Add timestamps
        rowData.push(new Date(response.first_submitted_at).toLocaleString());
        rowData.push(new Date(response.last_submitted_at).toLocaleString());
        
        csvRows.push(rowData.map(escapeCSV).join(','));
      });
      
      // Create CSV content
      const csvContent = csvRows.join('\n');
      
      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      const fileName = `sponsor-data-${responses[0].competition_name.replace(/\s+/g, '-')}-${Date.now()}.csv`;
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setExportMessage({ 
        type: 'success', 
        text: `Exported ${responses.length} user response${responses.length !== 1 ? 's' : ''} successfully!` 
      });
    } catch (err) {
      setExportMessage({ type: 'error', text: 'Failed to export data. Please try again.' });
    } finally {
      setExportingData(false);
    }
  };

  const toDateInput = (date) => {
    const local = new Date(date);
    local.setMinutes(local.getMinutes() - local.getTimezoneOffset());
    return local.toISOString().split("T")[0];
  };

  const getRangeFromPreset = (preset) => {
    const now = new Date();
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    const start = new Date(end);

    if (preset === "today") {
      start.setHours(0, 0, 0, 0);
    } else if (preset === "last_7_days") {
      start.setDate(end.getDate() - 6);
      start.setHours(0, 0, 0, 0);
    } else if (preset === "last_30_days") {
      start.setDate(end.getDate() - 29);
      start.setHours(0, 0, 0, 0);
    } else if (preset === "this_month") {
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
    } else if (preset === "this_year") {
      start.setMonth(0, 1);
      start.setHours(0, 0, 0, 0);
    }

    return { startDate: toDateInput(start), endDate: toDateInput(end) };
  };

  const parseRangeBounds = () => {
    const start = analyticsDateRange.startDate
      ? new Date(`${analyticsDateRange.startDate}T00:00:00`)
      : new Date(new Date().setDate(new Date().getDate() - 29));
    const end = analyticsDateRange.endDate
      ? new Date(`${analyticsDateRange.endDate}T23:59:59.999`)
      : new Date();
    return { start, end };
  };

  const formatShortDate = (dateValue) => {
    if (!dateValue) return "-";
    return new Date(dateValue).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getWeekStartISO = (dateValue) => {
    const date = new Date(dateValue);
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    const weekStart = new Date(date);
    weekStart.setDate(diff);
    weekStart.setHours(0, 0, 0, 0);
    return weekStart.toISOString().slice(0, 10);
  };

  const firstDefinedValue = (obj, keys, fallback = null) => {
    for (const key of keys) {
      if (obj && obj[key] !== undefined && obj[key] !== null) return obj[key];
    }
    return fallback;
  };

  const toNumber = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const pickRowDate = (row, keys) => {
    const value = firstDefinedValue(row, keys);
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const filterByDateRange = (rows, keys, start, end) => {
    return (rows || []).filter((row) => {
      const date = pickRowDate(row, keys);
      if (!date) return false;
      return date >= start && date <= end;
    });
  };

  const safeFetchView = async (viewName) => {
    try {
      const { data, error } = await supabase.from(viewName).select("*").limit(5000);
      if (error) return null;
      return data || [];
    } catch (_err) {
      return null;
    }
  };

  const loadCurrentCompetitionAnalytics = async () => {
    const safeFetch = async (queryBuilder) => {
      try {
        const { data, error } = await queryBuilder();
        if (error) return [];
        return data || [];
      } catch (_err) {
        return [];
      }
    };

    const competitions = await safeFetch(() =>
      supabase
        .from("competitions")
        .select("id, name, league, season, type, current_match_day, registration_status, status")
        .eq("status", "active")
        .neq("type", "scout")
        .order("created_at", { ascending: false })
    );

    if (!competitions.length) return [];

    const competitionIds = competitions.map((comp) => comp.id);

    const competitionKeys = Array.from(
      new Set(
        competitions
          .filter((comp) => comp.league && comp.season)
          .map((comp) => `${comp.league}|||${comp.season}`)
      )
    );
    const leagues = Array.from(new Set(competitionKeys.map((key) => key.split("|||")[0])));
    const seasons = Array.from(new Set(competitionKeys.map((key) => key.split("|||")[1])));

    const [registrations, selections, fixtures, knockoutLeaderboardRows] = await Promise.all([
      safeFetch(() =>
        supabase
          .from("user_selections")
          .select("competition_id, user_id")
          .in("competition_id", competitionIds)
      ),
      safeFetch(() =>
        supabase
          .from("team_selections")
          .select("competition_id, match_day, selected_team, user_id")
          .in("competition_id", competitionIds)
      ),
      leagues.length && seasons.length
        ? safeFetch(() =>
            supabase
              .from("fixtures")
              .select("league, season, home_team, away_team")
              .in("league", leagues)
              .in("season", seasons)
          )
        : Promise.resolve([]),
      safeFetch(() =>
        supabase
          .from("knockout_leaderboard_view")
          .select("competition_id, user_id, status")
          .in("competition_id", competitionIds)
      ),
    ]);

    const activeKnockoutUsersByCompetition = {};
    (knockoutLeaderboardRows || []).forEach((row) => {
      if (!row?.competition_id || !row?.user_id) return;
      const status = String(row.status || "").toLowerCase();
      const isActive = status === "still in" || status === "active";
      if (!isActive) return;
      if (!activeKnockoutUsersByCompetition[row.competition_id]) {
        activeKnockoutUsersByCompetition[row.competition_id] = new Set();
      }
      activeKnockoutUsersByCompetition[row.competition_id].add(row.user_id);
    });

    const teamsByLeagueSeason = {};
    (fixtures || []).forEach((fixture) => {
      const key = `${fixture.league}|||${fixture.season}`;
      if (!teamsByLeagueSeason[key]) {
        teamsByLeagueSeason[key] = new Set();
      }
      if (fixture.home_team) teamsByLeagueSeason[key].add(fixture.home_team);
      if (fixture.away_team) teamsByLeagueSeason[key].add(fixture.away_team);
    });

    const rows = competitions
      .map((comp) => {
        const currentMatchDay = Number(comp.current_match_day || 0);
        if (!currentMatchDay) return null;

        const compRegistrations = registrations.filter((r) => r.competition_id === comp.id);
        const regUsersSet = new Set(compRegistrations.map((r) => r.user_id));

        const compSelections = selections.filter(
          (s) =>
            s.competition_id === comp.id &&
            Number(s.match_day) === currentMatchDay &&
            s.selected_team
        );

        const selectionsMade = compSelections.length;
        const submittedUsers = new Set(compSelections.map((s) => s.user_id)).size;
        const registeredUsers = regUsersSet.size;
        const activeUsers = comp.type === "knockout"
          ? (activeKnockoutUsersByCompetition[comp.id]?.size || 0)
          : registeredUsers;
        const submissionRate = activeUsers
          ? Number(((submittedUsers / activeUsers) * 100).toFixed(1))
          : 0;

        const teamCounts = compSelections.reduce((acc, selection) => {
          const team = selection.selected_team;
          acc[team] = (acc[team] || 0) + 1;
          return acc;
        }, {});

        const leagueSeasonKey = `${comp.league}|||${comp.season}`;
        const leagueTeams = teamsByLeagueSeason[leagueSeasonKey]
          ? Array.from(teamsByLeagueSeason[leagueSeasonKey]).sort((a, b) => a.localeCompare(b))
          : [];

        // Fallback to selected teams if fixture-based league team list is unavailable.
        const teamsToDisplay = leagueTeams.length
          ? leagueTeams
          : Object.keys(teamCounts).sort((a, b) => a.localeCompare(b));

        const teamBreakdown = teamsToDisplay
          .map((team) => {
            const count = Number(teamCounts[team] || 0);
            return {
            team,
            count,
            percentage: selectionsMade
              ? Number(((Number(count) / selectionsMade) * 100).toFixed(1))
              : 0,
            };
          })
          .sort((a, b) => {
            if (b.count !== a.count) return b.count - a.count;
            return a.team.localeCompare(b.team);
          });

        return {
          competitionId: comp.id,
          competitionName: comp.name,
          league: comp.league,
          currentMatchDay,
          registeredUsers,
          activeUsers,
          selectionsMade: submittedUsers,
          submissionRate,
          teamBreakdown,
        };
      })
      .filter(Boolean);

    return rows;
  };

  const loadAnalyticsFromViews = async (start, end) => {
    const [
      dailyView,
      weeklyView,
      matchdayView,
      leagueView,
      cohortsView,
      competitionCurrentWeekRows,
    ] = await Promise.all([
      safeFetchView("analytics_selections_daily"),
      safeFetchView("analytics_selections_weekly"),
      safeFetchView("analytics_selections_by_matchday"),
      safeFetchView("analytics_league_engagement"),
      safeFetchView("analytics_user_cohorts"),
      loadCurrentCompetitionAnalytics(),
    ]);

    if (!dailyView || !weeklyView || !matchdayView || !leagueView || !cohortsView) {
      return false;
    }

    const dailyRows = filterByDateRange(dailyView, ["date", "selection_date", "day", "created_at"], start, end);
    const weeklyRows = filterByDateRange(weeklyView, ["week_start", "week_start_date", "period_start", "created_at"], start, end);
    const matchdayRowsInRange = filterByDateRange(
      matchdayView,
      ["match_day_date", "date", "created_at", "first_kickoff_time"],
      start,
      end
    );
    const matchdayRowsSource = matchdayRowsInRange.length ? matchdayRowsInRange : matchdayView;

    const latestWeeklyRow = [...weeklyRows].sort((a, b) => {
      const aDate = pickRowDate(a, ["week_start", "week_start_date", "period_start", "created_at"]) || new Date(0);
      const bDate = pickRowDate(b, ["week_start", "week_start_date", "period_start", "created_at"]) || new Date(0);
      return bDate - aDate;
    })[0];

    const totalSelectionsFromDaily = dailyRows.reduce(
      (sum, row) => sum + toNumber(firstDefinedValue(row, ["total_selections", "selections", "selection_count"], 0)),
      0
    );

    const uniqueSelectors = toNumber(
      firstDefinedValue(latestWeeklyRow, ["unique_selectors", "weekly_active_selectors", "active_selectors"], 0)
    );

    const totalSelections = totalSelectionsFromDaily || toNumber(
      firstDefinedValue(latestWeeklyRow, ["total_selections", "selections", "selection_count"], 0)
    );

    const avgSelectionsPerSelector = uniqueSelectors
      ? Number((totalSelections / uniqueSelectors).toFixed(2))
      : 0;

    const weeklyTrend = weeklyRows
      .map((row, index) => ({
        weekStart:
          firstDefinedValue(row, ["week_start", "week_start_date", "period_start"]) ||
          `${index}`,
        weeklyActiveSelectors: toNumber(
          firstDefinedValue(row, ["weekly_active_selectors", "unique_selectors", "active_selectors"], 0)
        ),
      }))
      .sort((a, b) => new Date(a.weekStart) - new Date(b.weekStart));

    const matchDayRows = matchdayRowsSource
      .map((row) => {
        const rowTotal = toNumber(firstDefinedValue(row, ["total_selections", "selections", "selection_count"], 0));
        const rowUnique = toNumber(firstDefinedValue(row, ["unique_selectors", "selectors", "active_selectors"], 0));
        return {
          matchDay: firstDefinedValue(row, ["match_day", "matchday", "match_day_identifier"], "Unknown"),
          totalSelections: rowTotal,
          uniqueSelectors: rowUnique,
          avgSelectionsPerSelector: rowUnique ? Number((rowTotal / rowUnique).toFixed(2)) : 0,
        };
      })
      .sort((a, b) => Number(a.matchDay) - Number(b.matchDay));

    const leagueRows = leagueView
      .map((row) => {
        const rowTotal = toNumber(firstDefinedValue(row, ["total_selections", "selections", "selection_count"], 0));
        const rowUnique = toNumber(firstDefinedValue(row, ["unique_selectors", "selectors", "active_selectors"], 0));
        const rowAverage = toNumber(
          firstDefinedValue(row, ["avg_selections_per_selector", "average_selections_per_selector"], 0)
        );
        return {
          league: firstDefinedValue(row, ["league", "league_name"], "Unknown"),
          uniqueSelectors: rowUnique,
          totalSelections: rowTotal,
          avgSelectionsPerSelector: rowAverage || (rowUnique ? Number((rowTotal / rowUnique).toFixed(2)) : 0),
          activeCompetitions: toNumber(firstDefinedValue(row, ["active_competitions", "active_competitions_in_league"], 0)),
          engagementScore: toNumber(
            firstDefinedValue(row, ["league_engagement_score", "engagement_score"], rowUnique * (rowAverage || 0))
          ),
        };
      })
      .sort((a, b) => b.engagementScore - a.engagementScore);

    const latestCohortRow = [...cohortsView].sort((a, b) => {
      const aDate = pickRowDate(a, ["date", "period_start", "created_at"]) || new Date(0);
      const bDate = pickRowDate(b, ["date", "period_start", "created_at"]) || new Date(0);
      return bDate - aDate;
    })[0] || {};

    const { data: profiles = [] } = await supabase.from("profiles").select("id, created_at");
    const { data: profilesInRange = [] } = await supabase
      .from("profiles")
      .select("id, created_at")
      .gte("created_at", start.toISOString())
      .lte("created_at", end.toISOString());
    const { data: competitions = [] } = await supabase
      .from("competitions")
      .select("id, registration_status, created_at, is_sponsored");

    const { data: sponsoredAnswers = [] } = await supabase
      .from("sponsor_question_answers")
      .select("id, competition_id, created_at")
      .gte("created_at", start.toISOString())
      .lte("created_at", end.toISOString());

    const sponsoredEntries = dailyRows.reduce(
      (sum, row) => sum + toNumber(firstDefinedValue(row, ["sponsored_selections", "sponsored_entries"], 0)),
      0
    );
    const sponsorQuestionnaireCompletionRate = sponsoredEntries
      ? Number((((sponsoredAnswers || []).length / sponsoredEntries) * 100).toFixed(1))
      : 0;

    const totalCompetitions = (competitions || []).length;
    const activeCompetitions = (competitions || []).filter(
      (comp) => comp.status === "active"
    ).length;
    const completedCompetitionsInRange = (competitions || []).filter((comp) => {
      const created = comp.created_at ? new Date(comp.created_at) : null;
      return comp.registration_status === "complete" && created && created >= start && created <= end;
    }).length;

    setAnalyticsData({
      weeklyActiveSelectors: weeklyTrend.length
        ? weeklyTrend[weeklyTrend.length - 1].weeklyActiveSelectors
        : 0,
      totalSelections,
      uniqueSelectors,
      avgSelectionsPerSelector,
      activeUsersDAU: toNumber(firstDefinedValue(latestCohortRow, ["dau", "active_users_dau"], 0)),
      activeUsersWAU: toNumber(firstDefinedValue(latestCohortRow, ["wau", "active_users_wau"], 0)),
      activeUsersMAU: toNumber(firstDefinedValue(latestCohortRow, ["mau", "active_users_mau"], 0)),
      totalUsers: (profiles || []).length,
      newUsers: (profilesInRange || []).length,
      totalCompetitions,
      activeCompetitions,
      completedCompetitionsInRange,
      returningSelectors4Week: toNumber(
        firstDefinedValue(latestCohortRow, ["returning_selectors_4_week", "returning_selectors"], 0)
      ),
      firstTimeSelectors: toNumber(
        firstDefinedValue(latestCohortRow, ["first_time_selectors", "new_selectors"], 0)
      ),
      dormantUsers30Day: toNumber(
        firstDefinedValue(latestCohortRow, ["dormant_users_30_day", "dormant_users"], 0)
      ),
      sponsoredEntries,
      sponsorQuestionnaireCompletionRate,
      weeklyTrend,
      matchDayRows,
      leagueRows,
      competitionCurrentWeekRows,
    });

    return true;
  };

  const loadUsersTabData = async () => {
    setUsersLoading(true);
    try {
      const { start, end } = parseRangeBounds();

      const { data: authProfileStats } = await supabase.rpc("get_admin_auth_profile_stats");
      const firstStatsRow = Array.isArray(authProfileStats) ? authProfileStats[0] : null;
      const authSignups = Number(firstStatsRow?.total_auth_users || 0);
      const missingProfiles = Number(firstStatsRow?.missing_profiles || 0);

      const { count: totalUsersCount } = await supabase
        .from("profiles")
        .select("id", { count: "exact", head: true });

      const { count: newUsersCount } = await supabase
        .from("profiles")
        .select("id", { count: "exact", head: true })
        .gte("created_at", start.toISOString())
        .lte("created_at", end.toISOString());

      const { data: usersData, error: usersError } = await supabase
        .from("profiles")
        .select("id, username, email, role, created_at, time_zone")
        .order("created_at", { ascending: false })
        .limit(250);

      if (usersError) {
        throw usersError;
      }

      const adminUsers = (usersData || []).filter((u) => u.role === "admin").length;
      const timeZoneCounts = (usersData || []).reduce((acc, userRow) => {
        const tz = userRow.time_zone || "Not set";
        acc[tz] = (acc[tz] || 0) + 1;
        return acc;
      }, {});
      const timeZoneRows = Object.entries(timeZoneCounts)
        .map(([timeZone, count]) => ({
          timeZone,
          count: Number(count),
        }))
        .sort((a, b) => b.count - a.count);

      setUsersSummary({
        authSignups,
        totalUsers: totalUsersCount || 0,
        newUsersInRange: newUsersCount || 0,
        adminUsers,
        missingProfiles,
      });
      setUsersList(usersData || []);
      setUsersByTimeZone(timeZoneRows);
    } catch (err) {
      notify("Failed to load users data", "error");
    } finally {
      setUsersLoading(false);
    }
  };

  const loadAnalyticsTabData = async () => {
    setAnalyticsLoading(true);
    setAnalyticsError("");

    const { start, end } = parseRangeBounds();
    const previousStart = new Date(start);
    previousStart.setDate(previousStart.getDate() - 28);

    try {
      const loadedFromViews = await loadAnalyticsFromViews(start, end);
      if (loadedFromViews) {
        setAnalyticsDataSource("cached_views");
        setAnalyticsLastUpdated(new Date().toISOString());
        return;
      }

      setAnalyticsDataSource("raw_tables");

      const safeFetch = async (queryBuilder) => {
        try {
          const { data, error } = await queryBuilder();
          if (error) return [];
          return data || [];
        } catch (_err) {
          return [];
        }
      };

      const [
        profiles,
        profilesInRange,
        teamSelectionsInRange,
        userSelectionsInRange,
        scoutEntriesInRange,
        teamSelectionsAll,
        userSelectionsAll,
        scoutEntriesAll,
        competitions,
        sponsoredAnswers,
        competitionCurrentWeekRows,
      ] = await Promise.all([
        safeFetch(() => supabase.from("profiles").select("id, created_at")),
        safeFetch(() =>
          supabase
            .from("profiles")
            .select("id, created_at")
            .gte("created_at", start.toISOString())
            .lte("created_at", end.toISOString())
        ),
        safeFetch(() =>
          supabase
            .from("team_selections")
            .select("user_id, created_at, match_day, competition_id")
            .gte("created_at", start.toISOString())
            .lte("created_at", end.toISOString())
        ),
        safeFetch(() =>
          supabase
            .from("user_selections")
            .select("user_id, created_at, competition_id")
            .gte("created_at", start.toISOString())
            .lte("created_at", end.toISOString())
        ),
        safeFetch(() =>
          supabase
            .from("scout_entries")
            .select("user_id, created_at, competition_id")
            .gte("created_at", start.toISOString())
            .lte("created_at", end.toISOString())
        ),
        safeFetch(() => supabase.from("team_selections").select("user_id, created_at")),
        safeFetch(() => supabase.from("user_selections").select("user_id, created_at")),
        safeFetch(() => supabase.from("scout_entries").select("user_id, created_at")),
        safeFetch(() =>
          supabase.from("competitions").select("id, league, type, registration_status, is_sponsored, created_at")
        ),
        safeFetch(() =>
          supabase
            .from("sponsor_question_answers")
            .select("id, competition_id, created_at")
            .gte("created_at", start.toISOString())
            .lte("created_at", end.toISOString())
        ),
        loadCurrentCompetitionAnalytics(),
      ]);

      const rangeEvents = [
        ...(teamSelectionsInRange || []).map((row) => ({
          source: "team",
          user_id: row.user_id,
          timestamp: row.created_at,
          match_day: row.match_day,
          competition_id: row.competition_id,
        })),
        ...(userSelectionsInRange || []).map((row) => ({
          source: "user",
          user_id: row.user_id,
          timestamp: row.created_at,
          competition_id: row.competition_id,
        })),
        ...(scoutEntriesInRange || []).map((row) => ({
          source: "scout",
          user_id: row.user_id,
          timestamp: row.created_at,
          competition_id: row.competition_id,
        })),
      ].filter((event) => event.user_id && event.timestamp);

      const allEvents = [
        ...(teamSelectionsAll || []).map((row) => ({ user_id: row.user_id, timestamp: row.created_at })),
        ...(userSelectionsAll || []).map((row) => ({ user_id: row.user_id, timestamp: row.created_at })),
        ...(scoutEntriesAll || []).map((row) => ({ user_id: row.user_id, timestamp: row.created_at })),
      ].filter((event) => event.user_id && event.timestamp);

      const uniqueSelectors = new Set(rangeEvents.map((event) => event.user_id));
      const totalSelections = rangeEvents.length;
      const avgSelectionsPerSelector = uniqueSelectors.size
        ? Number((totalSelections / uniqueSelectors.size).toFixed(2))
        : 0;

      const endOfRange = end.getTime();
      const msDay = 24 * 60 * 60 * 1000;
      const withinDays = (timestamp, days) => endOfRange - new Date(timestamp).getTime() <= days * msDay;

      const dailyActive = new Set(allEvents.filter((event) => withinDays(event.timestamp, 1)).map((e) => e.user_id)).size;
      const weeklyActive = new Set(allEvents.filter((event) => withinDays(event.timestamp, 7)).map((e) => e.user_id)).size;
      const monthlyActive = new Set(allEvents.filter((event) => withinDays(event.timestamp, 30)).map((e) => e.user_id)).size;

      const weeklyMap = new Map();
      rangeEvents.forEach((event) => {
        const key = getWeekStartISO(event.timestamp);
        if (!weeklyMap.has(key)) {
          weeklyMap.set(key, new Set());
        }
        weeklyMap.get(key).add(event.user_id);
      });
      const weeklyTrend = Array.from(weeklyMap.entries())
        .map(([weekStart, users]) => ({ weekStart, weeklyActiveSelectors: users.size }))
        .sort((a, b) => new Date(a.weekStart) - new Date(b.weekStart));

      const competitionMap = new Map((competitions || []).map((comp) => [comp.id, comp]));

      const matchDayMap = new Map();
      rangeEvents
        .filter((event) => event.source === "team")
        .forEach((event) => {
          const key = event.match_day || "Unknown";
          if (!matchDayMap.has(key)) {
            matchDayMap.set(key, { totalSelections: 0, selectors: new Set() });
          }
          const row = matchDayMap.get(key);
          row.totalSelections += 1;
          row.selectors.add(event.user_id);
        });
      const matchDayRows = Array.from(matchDayMap.entries())
        .map(([matchDay, row]) => ({
          matchDay,
          totalSelections: row.totalSelections,
          uniqueSelectors: row.selectors.size,
          avgSelectionsPerSelector: row.selectors.size
            ? Number((row.totalSelections / row.selectors.size).toFixed(2))
            : 0,
        }))
        .sort((a, b) => Number(a.matchDay) - Number(b.matchDay));

      const leagueMap = new Map();
      rangeEvents.forEach((event) => {
        const competition = competitionMap.get(event.competition_id);
        const league = competition?.league || "Unknown";
        if (!leagueMap.has(league)) {
          leagueMap.set(league, { totalSelections: 0, selectors: new Set() });
        }
        const row = leagueMap.get(league);
        row.totalSelections += 1;
        row.selectors.add(event.user_id);
      });

      const activeCompetitionsByLeague = new Map();
      (competitions || []).forEach((comp) => {
        const league = comp.league || "Unknown";
        const isActive = comp.registration_status === "open" || comp.registration_status === "active";
        if (isActive) {
          activeCompetitionsByLeague.set(league, (activeCompetitionsByLeague.get(league) || 0) + 1);
        }
      });

      const leagueRows = Array.from(leagueMap.entries())
        .map(([league, row]) => {
          const unique = row.selectors.size;
          const average = unique ? Number((row.totalSelections / unique).toFixed(2)) : 0;
          return {
            league,
            uniqueSelectors: unique,
            totalSelections: row.totalSelections,
            avgSelectionsPerSelector: average,
            activeCompetitions: activeCompetitionsByLeague.get(league) || 0,
            engagementScore: Number((unique * average).toFixed(2)),
          };
        })
        .sort((a, b) => b.engagementScore - a.engagementScore);

      const usersInCurrentRange = new Set(
        rangeEvents
          .filter((event) => new Date(event.timestamp) >= start && new Date(event.timestamp) <= end)
          .map((event) => event.user_id)
      );
      const usersInPrevious4Weeks = new Set(
        allEvents
          .filter((event) => {
            const d = new Date(event.timestamp);
            return d >= previousStart && d < start;
          })
          .map((event) => event.user_id)
      );

      const returningSelectors4Week = Array.from(usersInCurrentRange).filter((id) => usersInPrevious4Weeks.has(id)).length;

      const firstSelectionByUser = new Map();
      allEvents.forEach((event) => {
        const current = firstSelectionByUser.get(event.user_id);
        const ts = new Date(event.timestamp).getTime();
        if (!current || ts < current) {
          firstSelectionByUser.set(event.user_id, ts);
        }
      });
      const firstTimeSelectors = Array.from(firstSelectionByUser.values()).filter((ts) => ts >= start.getTime() && ts <= end.getTime()).length;

      const activeLast30 = new Set(
        allEvents
          .filter((event) => endOfRange - new Date(event.timestamp).getTime() <= 30 * msDay)
          .map((event) => event.user_id)
      ).size;
      const dormantUsers30Day = Math.max(0, (profiles || []).length - activeLast30);

      const sponsoredCompetitionIds = new Set(
        (competitions || []).filter((comp) => comp.is_sponsored).map((comp) => comp.id)
      );
      const sponsoredEntries = rangeEvents.filter((event) => sponsoredCompetitionIds.has(event.competition_id)).length;
      const sponsorQuestionnaireCompletionRate = sponsoredEntries
        ? Number((((sponsoredAnswers || []).length / sponsoredEntries) * 100).toFixed(1))
        : 0;

      const totalCompetitions = (competitions || []).length;
      const activeCompetitions = (competitions || []).filter(
        (comp) => comp.registration_status === "open" || comp.registration_status === "active"
      ).length;
      const completedCompetitionsInRange = (competitions || []).filter((comp) => {
        const created = comp.created_at ? new Date(comp.created_at) : null;
        return (
          comp.registration_status === "complete" &&
          created &&
          created >= start &&
          created <= end
        );
      }).length;

      setAnalyticsData({
        weeklyActiveSelectors: weeklyActive,
        totalSelections,
        uniqueSelectors: uniqueSelectors.size,
        avgSelectionsPerSelector,
        activeUsersDAU: dailyActive,
        activeUsersWAU: weeklyActive,
        activeUsersMAU: monthlyActive,
        totalUsers: (profiles || []).length,
        newUsers: (profilesInRange || []).length,
        totalCompetitions,
        activeCompetitions,
        completedCompetitionsInRange,
        returningSelectors4Week,
        firstTimeSelectors,
        dormantUsers30Day,
        sponsoredEntries,
        sponsorQuestionnaireCompletionRate,
        weeklyTrend,
        matchDayRows,
        leagueRows,
        competitionCurrentWeekRows,
      });
      setAnalyticsLastUpdated(new Date().toISOString());
    } catch (err) {
      setAnalyticsError("Some analytics data could not be loaded. Check table/view availability.");
    } finally {
      setAnalyticsLoading(false);
    }
  };

  useEffect(() => {
    if (analyticsRangePreset === "custom") return;
    setAnalyticsDateRange(getRangeFromPreset(analyticsRangePreset));
  }, [analyticsRangePreset]);

  useEffect(() => {
    if (!authorized) return;
    if (activeAdminTab === "users") {
      loadUsersTabData();
    }
    if (activeAdminTab === "analytics") {
      loadAnalyticsTabData();
    }
  }, [authorized, activeAdminTab]);

  useEffect(() => {
    const rows = analyticsData.competitionCurrentWeekRows || [];
    if (rows.length === 0) {
      setSelectedAnalyticsCompetitionId("");
      return;
    }

    const exists = rows.some((row) => row.competitionId === selectedAnalyticsCompetitionId);
    if (!selectedAnalyticsCompetitionId || !exists) {
      setSelectedAnalyticsCompetitionId(rows[0].competitionId);
    }
  }, [analyticsData.competitionCurrentWeekRows, selectedAnalyticsCompetitionId]);

  const addOption = () => {
    if (!optionInput.trim()) return;
    
    if (editingQuestion) {
      setEditingQuestion({
        ...editingQuestion,
        options: [...editingQuestion.options, optionInput.trim()]
      });
    } else {
      setNewQuestion({
        ...newQuestion,
        options: [...newQuestion.options, optionInput.trim()]
      });
    }
    setOptionInput("");
  };

  const removeOption = (index) => {
    if (editingQuestion) {
      setEditingQuestion({
        ...editingQuestion,
        options: editingQuestion.options.filter((_, i) => i !== index)
      });
    } else {
      setNewQuestion({
        ...newQuestion,
        options: newQuestion.options.filter((_, i) => i !== index)
      });
    }
  };

  const adminTabs = [
    { id: "competition", label: "Competition Management" },
    { id: "sponsorship", label: "Sponsorship Management" },
    { id: "release", label: "Release Management" },
    { id: "legal", label: "Legal Management" },
    { id: "users", label: "Users" },
    { id: "analytics", label: "Analytics" },
    { id: "support", label: "Support Tickets" },
  ];

  const selectedCompetitionAnalyticsRow =
    analyticsData.competitionCurrentWeekRows.find(
      (row) => row.competitionId === selectedAnalyticsCompetitionId
    ) || null;

  if (loading) return <div className="p-6">Checking admin access...</div>;
  if (!authorized) return null;

  // Responsive admin panel layout
  return (
    <div className="flex min-h-screen bg-surface-canvas text-brand-text">
      {/* Sidebar for desktop */}
      <SidebarNavbar userRole={userRole} handleLogout={async () => { await supabase.auth.signOut(); navigate("/login"); }} />
      {/* Main content */}
      <div role="main" id="fc-main-content" className="flex-1 flex flex-col md:ml-64 pb-16 md:pb-0 fc-workspace">

          <h1 className="mb-6">Platform administration</h1>

          <FcTabs label="Platform administration" value={activeAdminTab} onValueChange={setActiveAdminTab} items={adminTabs}>

          {activeAdminTab === "competition" && (
            <>
          {/* 1. Update Fixtures Section - Select League & Season */}
          <FcCard as="div" padding="standard" className="mb-8 border p-4 rounded shadow-sm bg-surface-card">
            <h3 className="font-semibold mb-2 text-action-primary">Select League & Season</h3>
            <div className="flex flex-col md:flex-row gap-4">
              <FcSelect label="Select League" aria-label="Select League"
                value={selectedLeague}
                onChange={(e) => {
                  setSelectedLeague(e.target.value);
                }}
                className="block w-full border"
              >
                <option value="" disabled>
                  Select League
                </option>
                <option value="Premier League">Premier League</option>
                <option value="La Liga">La Liga</option>
                <option value="Serie A">Serie A</option>
                <option value="Bundesliga">Bundesliga</option>
                <option value="Ligue 1">Ligue 1</option>
                <option value="Championship">Championship</option>
                <option value="League One">League One</option>
                <option value="League Two">League Two</option>
                <option value="MLS">MLS</option>
                <option value="World Cup">World Cup</option>
              </FcSelect>
              <FcSelect label="Season" aria-label="Season"
                value={selectedSeason}
                onChange={(e) => {
                  setSelectedSeason(e.target.value);
                }}
                className="block w-full border"
              >
                <option value="" disabled>
                  Select Season
                </option>
                  <option value="2026/27">2026/27</option>
                <option value="2025/26">2025/26</option>
                <option value="2026">2026</option>
                <option value="2025">2025</option>
              </FcSelect>
            </div>
          </FcCard>

          {/* Matchday Navigation & Update Fixture Results */}
          {selectedLeague && selectedSeason && (
            <>
              <div className="mb-6 flex items-center gap-4">
                <h3 className="font-semibold text-action-primary mb-0">Matchday</h3>
                <FcButton variant="primary" type="submit"
                  className=""
                  onClick={() => {
                    if (currentMatchday > 1) setCurrentMatchday(currentMatchday - 1);
                  }}
                  disabled={currentMatchday <= 1}
                >
                  &#8592;
                </FcButton>
                <span className="font-bold text-lg">{currentMatchday ? `Matchday ${currentMatchday}` : "-"}</span>
                <FcButton variant="primary" type="submit"
                  className=""
                  onClick={() => {
                    if (currentMatchday < maxMatchday) setCurrentMatchday(currentMatchday + 1);
                  }}
                  disabled={currentMatchday >= maxMatchday}
                >
                  &#8594;
                </FcButton>
              </div>
              
              <FcCard as="div" padding="standard" className="mb-8 border p-4 rounded shadow-sm bg-surface-card">
                <h3 className="mb-4 font-semibold text-action-primary">Update Fixture Results</h3>
                <div className="space-y-4">
                  {fixtures.length === 0 && selectedLeague && selectedSeason && (
                    <div className="text-brand-text-muted">No fixtures found for this matchday.</div>
                  )}
                  {fixtures.map((fixture) => (
                    <div
                      key={fixture.id}
                      className="border p-4 rounded shadow-sm bg-gray-50 text-ui-green"
                    >
                      <div className="mb-2 font-medium">
                        <strong>{fixture.home_team}</strong> vs {" "}
                        <strong>{fixture.away_team}</strong> ( {new Date(fixture.kickoff_time).toLocaleString()} )
                      </div>
                      <div className="flex items-center space-x-2 mb-2">
                        {/* Show status badge for abandoned/postponed */}
                        {(fixture.status === 'abandoned' || fixture.status === 'postponed') ? (
                          <div className={`flex-1 px-3 py-2 rounded text-center font-bold ${
                            fixture.status === 'abandoned' 
                              ? "bg-red-100 text-ui-error border border-red-300"
                              : 'bg-yellow-100 text-yellow-700 border border-yellow-300'
                          }`}>
                            {fixture.status === 'abandoned' ? '🚫 Ab' : '⏸️ Po'}
                          </div>
                        ) : (
                          <>
                            <FcInput label="Home Score" aria-label="Home Score"
                              type="number"
                              min="0"
                              max="30"
                              placeholder="Home Score"
                              value={fixture.home_score ?? ""}
                              onChange={(e) => {
                                fixture.home_score = Number(e.target.value);
                                setFixtures([...fixtures]);
                              }}
                              disabled={fixture.result && !editStates[fixture.id]}
                              className="w-16 border"
                            />
                            <FcInput label="Away Score" aria-label="Away Score"
                              type="number"
                              min="0"
                              max="30"
                              placeholder="Away Score"
                              value={fixture.away_score ?? ""}
                              onChange={(e) => {
                                fixture.away_score = Number(e.target.value);
                                setFixtures([...fixtures]);
                              }}
                              disabled={fixture.result && !editStates[fixture.id]}
                              className="w-16 border"
                            />
                            {fixture.result && !editStates[fixture.id] ? (
                              <FcButton variant="primary" type="submit"
                                onClick={() =>
                                  setEditStates({ ...editStates, [fixture.id]: true })
                                }
                                className=""
                              >
                                Edit
                              </FcButton>
                            ) : (
                              <FcButton variant="primary" type="submit"
                                onClick={() =>
                                  updateFixture(
                                    fixture.id,
                                    fixture.home_score,
                                    fixture.away_score
                                  )
                                }
                                className=""
                              >
                                Save Result
                              </FcButton>
                            )}
                          </>
                        )}
                      </div>
                      <div className="text-sm">Result: {fixture.result || "Not set"}</div>
                      {fixture.result && (
                        <div className="text-xs text-brand-text-muted">
                          Previous result: {fixture.result}, Score: {
                            fixture.status === 'abandoned' ? 'Ab' : fixture.status === 'postponed' ? 'Po' : `${fixture.home_score} - ${fixture.away_score}`
                          }
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </FcCard>
            </>
          )}

          {/* 2. Competition Creation Section */}
          <FcCard as="div" padding="standard" className="mb-8 border p-4 rounded shadow-sm bg-surface-card">
            <h3 className="font-semibold mb-2 text-action-primary">Create New Competition</h3>
            <FcInput label="Competition Name" aria-label="Competition Name"
              type="text"
              placeholder="Competition Name"
              value={newCompetition.name}
              onChange={(e) =>
                setNewCompetition({ ...newCompetition, name: e.target.value })
              }
              className="mb-2 block w-full border"
            />
            <FcSelect label="Select Competition Type" aria-label="Select Competition Type"
              value={newCompetition.type}
              onChange={(e) =>
                setNewCompetition({ ...newCompetition, type: e.target.value })
              }
              className="mb-2 block w-full border"
            >
              <option value="" disabled>
                Select Competition Type
              </option>
              <option value="knockout">Knockout</option>
              <option value="season">Season</option>
              <option value="scout">Scout</option>
              <option value="world_stage">World Stage</option>
            </FcSelect>
            {newCompetition.type === 'knockout' && (
              <FcCard as="div" padding="standard" className="mb-2 p-3 border rounded bg-surface-card">
                <label className="block text-sm font-semibold mb-2 text-ui-green">Revival Strategy:</label>
                <div className="flex gap-4">
                  <label className="flex items-center cursor-pointer">
                    <FcInput aria-label="Free Pass"
                      type="radio"
                      name="revival_strategy"
                      value="free_pass"
                      checked={newCompetition.revival_strategy === 'free_pass'}
                      onChange={(e) =>
                        setNewCompetition({ ...newCompetition, revival_strategy: e.target.value })
                      }
                      className="mr-2"
                    />
                    <span className="text-ui-green">Free Pass</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <FcInput aria-label="Elimination"
                      type="radio"
                      name="revival_strategy"
                      value="elimination"
                      checked={newCompetition.revival_strategy === 'elimination'}
                      onChange={(e) =>
                        setNewCompetition({ ...newCompetition, revival_strategy: e.target.value })
                      }
                      className="mr-2"
                    />
                    <span className="text-ui-green">Elimination</span>
                  </label>
                </div>
              </FcCard>
            )}
            <FcSelect label="Select League" aria-label="Select League"
              value={newCompetition.league}
              onChange={(e) =>
                setNewCompetition({ ...newCompetition, league: e.target.value })
              }
              className="mb-2 block w-full border"
            >
              <option value="" disabled>
                Select League
              </option>
              <option value="Premier League">Premier League</option>
              <option value="La Liga">La Liga</option>
              <option value="Serie A">Serie A</option>
              <option value="Bundesliga">Bundesliga</option>
              <option value="Ligue 1">Ligue 1</option>
              <option value="Championship">Championship</option>
              <option value="League One">League One</option>
              <option value="League Two">League Two</option>
              <option value="MLS">MLS</option>
              <option value="World Cup">World Cup</option>
            </FcSelect>
            <FcSelect label="Select Season" aria-label="Select Season"
              value={newCompetition.season}
              onChange={(e) =>
                setNewCompetition({ ...newCompetition, season: e.target.value })
              }
              className="mb-2 block w-full border"
            >
              <option value="" disabled>
                Select Season
              </option>
              <option value="2026/27">2026/27</option>
              <option value="2025/26">2025/26</option>
                <option value="2026">2026</option>
              <option value="2025">2025</option>
            </FcSelect>
            <FcInput label="Start Date" aria-label="Start Date"
              type="date"
              placeholder="Start Date"
              value={newCompetition.startDate}
              onChange={(e) =>
                setNewCompetition({ ...newCompetition, startDate: e.target.value })
              }
              className="mb-2 block w-full border"
            />
            {newCompetition.type !== 'world_stage' && (
              <FcSelect label="Select Start Match Day" aria-label="Select Start Match Day"
                value={newCompetition.startMatchDay || ""}
                onChange={(e) =>
                  setNewCompetition({
                    ...newCompetition,
                    startMatchDay: e.target.value === '' ? '' : parseInt(e.target.value, 10),
                  })
                }
                className="mb-2 block w-full border"
              >
                <option value="">Select Start Match Day</option>
                {futureMatchdays.map((md) => (
                  <option key={md.match_day} value={md.match_day}>
                    Match Day {md.match_day} - {new Date(md.first_kickoff_time).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </option>
                ))}
              </FcSelect>
            )}

            {newCompetition.type === 'world_stage' && (
              <p className="mb-2 text-xs text-content-secondary">
                World Stage competitions do not require a start matchday.
              </p>
            )}

            {newCompetition.type === 'season' && (
              <TopGafferConfig
                availableMatchdays={futureMatchdays.filter(
                  (m) => m.match_day >= (Number(newCompetition.startMatchDay) || 0)
                )}
                value={newCompetition.topGaffer}
                onChange={(tg) => setNewCompetition({ ...newCompetition, topGaffer: tg })}
              />
            )}

            {/* Pontoon (Knockout only) — gated by PONTOON_ENABLED */}
            {PONTOON_ENABLED && newCompetition.type === 'knockout' && (
              <FcCard as="div" padding="standard" className="mb-2 p-3 border rounded bg-surface-card">
                <label className="flex items-center gap-2 text-ui-green font-medium">
                  <FcInput aria-label="Add Pontoon (free side-game for knocked-out players)"
                    type="checkbox"
                    checked={!!newCompetition.pontoon?.enabled}
                    onChange={(e) => setNewCompetition({
                      ...newCompetition,
                      pontoon: { ...(newCompetition.pontoon || {}), enabled: e.target.checked },
                    })}
                  />
                  Add Pontoon (free side-game for knocked-out players)
                </label>
                {newCompetition.pontoon?.enabled && (
                  <div className="mt-3">
                    <label className="block text-sm font-semibold text-ui-green mb-1">Pontoon match days</label>
                    <FcInput aria-label="Pontoon match days"
                      type="number"
                      min={1}
                      max={38}
                      value={newCompetition.pontoon?.matchDays ?? ""}
                      onChange={(e) => setNewCompetition({
                        ...newCompetition,
                        pontoon: { ...(newCompetition.pontoon || {}), matchDays: e.target.value },
                      })}
                      className="w-24 border"
                      placeholder="e.g. 7"
                    />
                    <p className="text-xs text-content-secondary mt-1">
                      Eliminated entries are auto-enrolled for the following match day. Ends after this many
                      match days or when the Knockout ends, whichever is sooner.
                    </p>
                  </div>
                )}
              </FcCard>
            )}

            <FcCard as="div" padding="standard" className="mb-2 p-3 border rounded bg-surface-card">
              <label className="flex items-center gap-2 text-ui-green font-medium">
                <FcInput aria-label="This competition is 18+"
                  type="checkbox"
                  checked={newCompetition.is_18_plus}
                  onChange={(e) => setNewCompetition({ ...newCompetition, is_18_plus: e.target.checked })}
                />
                This competition is 18+
              </label>
            </FcCard>
            
            {/* Sponsorship Section */}
            <div className="my-4 p-3 border rounded bg-gray-50">
              <label className="flex items-center gap-2 text-ui-green mb-3 font-medium">
                <FcInput aria-label="This is a sponsored competition"
                  type="checkbox"
                  checked={newCompetition.is_sponsored}
                  onChange={(e) => setNewCompetition({ ...newCompetition, is_sponsored: e.target.checked, sponsor_id: "" })}
                />
                This is a sponsored competition
              </label>
              
              {newCompetition.is_sponsored && (
                <>
                  <label className="block text-sm font-medium mb-1 text-ui-green">Select Sponsor *</label>
                  <FcSelect aria-label="-- Select a sponsor --"
                    value={newCompetition.sponsor_id}
                    onChange={(e) => setNewCompetition({ ...newCompetition, sponsor_id: e.target.value })}
                    className="mb-3 block w-full border"
                  >
                    <option value="">-- Select a sponsor --</option>
                    {sponsors.filter(s => s.active).map((sponsor) => (
                      <option key={sponsor.id} value={sponsor.id}>
                        {sponsor.name}
                      </option>
                    ))}
                  </FcSelect>
                  
                  <label className="block text-sm font-medium mb-1 text-ui-green">Sponsor Coverage (Matchdays)</label>
                  <FcInput aria-label="Number of matchdays sponsored"
                    type="number"
                    min="1"
                    max="38"
                    placeholder="Number of matchdays sponsored"
                    value={newCompetition.sponsor_matchday_count}
                    onChange={(e) => setNewCompetition({ ...newCompetition, sponsor_matchday_count: parseInt(e.target.value) || 1 })}
                    className="mb-2 block w-full border"
                  />
                  <p className="text-xs text-content-secondary mb-2">
                    {newCompetition.type === 'world_stage'
                      ? 'Sponsor coverage matchday range is not used for World Stage competitions.'
                      : `This sponsor will cover matchdays ${newCompetition.startMatchDay} to ${newCompetition.startMatchDay + newCompetition.sponsor_matchday_count - 1}`}
                  </p>
                  
                  {sponsors.filter(s => s.active).length === 0 && (
                    <p className="text-sm text-orange-600 mt-2">
                      ⚠️ No active sponsors found. Please create a sponsor first in the "Sponsor Management" section above.
                    </p>
                  )}
                </>
              )}
            </div>
            
            <FcButton variant="primary" type="submit"
              onClick={createCompetition}
              className=""
            >
              Create Competition
            </FcButton>
          </FcCard>

          {/* 3. CSV Upload Section */}
          <FcCard as="div" padding="standard" className="mb-8 border p-4 rounded shadow-sm bg-surface-card">
            <h3 className="font-semibold mb-2 text-action-primary">Bulk Upload Fixtures (CSV)</h3>
            <FcInput aria-label="Bulk Upload Fixtures (CSV)"
              type="file"
              accept=".csv"
              onChange={handleCsvFileChange}
              disabled={csvUploading}
              className="mb-2 block w-full border"
            />
            {csvFile && (
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-brand-text">{csvFile.name}</span>
                <FcButton variant="destructive" type="submit"
                  onClick={() => setCsvFile(null)}
                  className=""
                  disabled={csvUploading}
                >
                  Remove
                </FcButton>
              </div>
            )}
            <FcButton variant="primary" type="submit"
              onClick={handleCommitCsvUpload}
              disabled={!csvFile || csvUploading}
              className="mb-2"
            >
              Commit Upload
            </FcButton>
            {csvUploading && <div className="text-brand-text-muted">Uploading...</div>}
            {csvUploadMessage && <div className="text-brand-accent-from font-semibold">{csvUploadMessage}</div>}
            <div className="text-xs text-brand-text-muted mt-2">
              <strong>CSV columns required:</strong> league, season, match_day, home_team, away_team, kickoff_time, status (optional: id for upsert)
            </div>
          </FcCard>

          </>
          )}

          {activeAdminTab === "release" && (
            <>
          {/* 4. Release Notes Management Section */}
          <FcCard as="div" padding="standard" className="mb-8 border p-4 rounded shadow-sm bg-surface-card">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-action-primary">Release Notes Management</h3>
              <FcButton variant="tertiary" type="submit"
                onClick={() => setShowReleaseNotesForm(!showReleaseNotesForm)}
                className=""
              >
                {showReleaseNotesForm ? 'Cancel' : 'Add New Release'}
              </FcButton>
            </div>

            {/* New Release Note Form */}
            {showReleaseNotesForm && (
              <div className="mb-6 p-4 border rounded bg-gray-50">
                <h4 className="font-semibold mb-3 text-ui-green">Create New Release Note</h4>
                <FcInput label="Version (e.g., v2.1.0)" aria-label="Version (e.g., v2.1.0)"
                  type="text"
                  placeholder="Version (e.g., v2.1.0)"
                  value={newReleaseNote.version}
                  onChange={(e) => setNewReleaseNote({ ...newReleaseNote, version: e.target.value })}
                  className="mb-2 block w-full border"
                />
                <FcInput label="Release Title" aria-label="Release Title"
                  type="text"
                  placeholder="Release Title"
                  value={newReleaseNote.title}
                  onChange={(e) => setNewReleaseNote({ ...newReleaseNote, title: e.target.value })}
                  className="mb-2 block w-full border"
                />
                <FcTextarea label="Release content (Markdown supported)" aria-label="Release content (Markdown supported)"
                  placeholder="Release content (Markdown supported)"
                  value={newReleaseNote.content}
                  onChange={(e) => setNewReleaseNote({ ...newReleaseNote, content: e.target.value })}
                  rows="6"
                  className="mb-2 block w-full border resize-vertical"
                />
                <div className="flex flex-col md:flex-row gap-2 mb-3">
                  <FcInput aria-label="Publish immediately"
                    type="date"
                    value={newReleaseNote.release_date}
                    onChange={(e) => setNewReleaseNote({ ...newReleaseNote, release_date: e.target.value })}
                    className="border"
                  />
                  <label className="flex items-center gap-2 text-ui-green">
                    <FcInput aria-label="Publish immediately"
                      type="checkbox"
                      checked={newReleaseNote.is_published}
                      onChange={(e) => setNewReleaseNote({ ...newReleaseNote, is_published: e.target.checked })}
                    />
                    Publish immediately
                  </label>
                </div>
                <FcButton variant="primary" type="submit"
                  onClick={handleCreateReleaseNote}
                  className=""
                >
                  Create Release Note
                </FcButton>
              </div>
            )}

            {/* Edit Release Note Form */}
            {editingReleaseNote && (
              <div className="mb-6 p-4 border rounded bg-surface-canvas">
                <h4 className="font-semibold mb-3 text-ui-green">Edit Release Note</h4>
                <FcInput label="Version (e.g., v2.1.0)" aria-label="Version (e.g., v2.1.0)"
                  type="text"
                  placeholder="Version (e.g., v2.1.0)"
                  value={editingReleaseNote.version}
                  onChange={(e) => setEditingReleaseNote({ ...editingReleaseNote, version: e.target.value })}
                  className="mb-2 block w-full border"
                />
                <FcInput label="Release Title" aria-label="Release Title"
                  type="text"
                  placeholder="Release Title"
                  value={editingReleaseNote.title}
                  onChange={(e) => setEditingReleaseNote({ ...editingReleaseNote, title: e.target.value })}
                  className="mb-2 block w-full border"
                />
                <FcTextarea label="Release content (Markdown supported)" aria-label="Release content (Markdown supported)"
                  placeholder="Release content (Markdown supported)"
                  value={editingReleaseNote.content}
                  onChange={(e) => setEditingReleaseNote({ ...editingReleaseNote, content: e.target.value })}
                  rows="6"
                  className="mb-2 block w-full border resize-vertical"
                />
                <div className="flex flex-col md:flex-row gap-2 mb-3">
                  <FcInput aria-label="Published"
                    type="date"
                    value={editingReleaseNote.release_date}
                    onChange={(e) => setEditingReleaseNote({ ...editingReleaseNote, release_date: e.target.value })}
                    className="border"
                  />
                  <label className="flex items-center gap-2 text-ui-green">
                    <FcInput aria-label="Published"
                      type="checkbox"
                      checked={editingReleaseNote.is_published}
                      onChange={(e) => setEditingReleaseNote({ ...editingReleaseNote, is_published: e.target.checked })}
                    />
                    Published
                  </label>
                </div>
                <div className="flex gap-2">
                  <FcButton variant="primary" type="submit"
                    onClick={handleUpdateReleaseNote}
                    className=""
                  >
                    Update Release Note
                  </FcButton>
                  <FcButton variant="tertiary" type="submit"
                    onClick={() => setEditingReleaseNote(null)}
                    className=""
                  >
                    Cancel
                  </FcButton>
                </div>
              </div>
            )}

            {/* Release Notes List */}
            <div className="space-y-3">
              {releaseNotes.length === 0 ? (
                <p className="text-content-secondary text-center py-4">No release notes found. Create your first one!</p>
              ) : (
                releaseNotes.map((note) => (
                  <div key={note.id} className="border p-4 rounded bg-gray-50">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-2">
                      <div>
                        <h4 className="font-semibold text-ui-green">
                          {note.version} - {note.title}
                        </h4>
                        <p className="text-sm text-content-secondary">
                          Release Date: {new Date(note.release_date).toLocaleDateString()} | 
                          Status: <span className={note.is_published ? "text-ui-success" : "text-ui-error"}>
                            {note.is_published ? 'Published' : 'Draft'}
                          </span>
                        </p>
                      </div>
                      <div className="flex gap-2 mt-2 md:mt-0">
                        <FcButton aria-pressed={editingReleaseNote === note} variant="primary" type="submit"
                          onClick={() => setEditingReleaseNote(note)}
                          className=""
                        >
                          Edit
                        </FcButton>
                        <FcButton variant="primary" type="submit"
                          onClick={() => togglePublishStatus(note.id, note.is_published)}
                          className={`${
                            note.is_published 
                              ? ""
                              : ""
                          }`}
                        >
                          {note.is_published ? 'Unpublish' : 'Publish'}
                        </FcButton>
                        <FcButton variant="destructive" type="submit"
                          onClick={() => handleDeleteReleaseNote(note.id)}
                          className=""
                        >
                          Delete
                        </FcButton>
                      </div>
                    </div>
                    <FcCard as="div" padding="standard" className="text-sm text-content-primary bg-surface-card p-2 rounded border">
                      <pre className="whitespace-pre-wrap font-sans">{note.content}</pre>
                    </FcCard>
                  </div>
                ))
              )}
            </div>
          </FcCard>

            </>

          )}

          {activeAdminTab === "sponsorship" && (
            <>
          {/* 5. Sponsor Management Section */}
          <FcCard as="div" padding="standard" className="mb-8 border p-4 rounded shadow-sm bg-surface-card">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-action-primary">Sponsor Management</h3>
              <FcButton variant="tertiary" type="submit"
                onClick={() => setShowSponsorForm(!showSponsorForm)}
                className=""
              >
                {showSponsorForm ? 'Cancel' : 'Add New Sponsor'}
              </FcButton>
            </div>

            {/* New Sponsor Form */}
            {showSponsorForm && (
              <div className="mb-6 p-4 border rounded bg-gray-50">
                <h4 className="font-semibold mb-3 text-ui-green">Create New Sponsor</h4>
                <FcInput aria-label="Sponsor Name *"
                  type="text"
                  placeholder="Sponsor Name *"
                  value={newSponsor.name}
                  onChange={(e) => setNewSponsor({ ...newSponsor, name: e.target.value })}
                  className="mb-2 block w-full border"
                />
                
                {/* Logo Upload Section */}
                <FcCard as="div" padding="standard" className="mb-3 p-3 border rounded bg-surface-card">
                  <label className="block text-sm font-medium mb-2 text-ui-green">Sponsor Logo</label>
                  
                  {!useUrlInput ? (
                    <>
                      <FcInput aria-label="Sponsor Logo"
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                        onChange={handleLogoFileChange}
                        className="mb-2 block w-full file:mr-4 file:border-0"
                      />
                      <p className="text-xs text-content-secondary mb-2">
                        Max 2MB • PNG, JPG, WebP, SVG • 200-1000px
                      </p>
                      {logoPreview && (
                        <div className="mb-2">
                          <img src={logoPreview} alt="Logo preview" className="h-24 object-contain border rounded p-2" />
                        </div>
                      )}
                      <FcButton variant="tertiary"
                        type="button"
                        onClick={() => setUseUrlInput(true)}
                        className="hover:underline"
                      >
                        Or enter URL instead
                      </FcButton>
                    </>
                  ) : (
                    <>
                      <FcInput aria-label="Logo URL (e.g., https://example.com/logo.png)"
                        type="url"
                        placeholder="Logo URL (e.g., https://example.com/logo.png)"
                        value={newSponsor.logo_url}
                        onChange={(e) => setNewSponsor({ ...newSponsor, logo_url: e.target.value })}
                        className="mb-2 block w-full border"
                      />
                      <p className="text-xs text-orange-600 mb-2">
                        ⚠️ External URLs may have security implications. Upload is recommended.
                      </p>
                      <FcButton variant="tertiary"
                        type="button"
                        onClick={() => { setUseUrlInput(false); setNewSponsor({ ...newSponsor, logo_url: '' }); }}
                        className="hover:underline"
                      >
                        Upload file instead
                      </FcButton>
                    </>
                  )}
                </FcCard>
                <FcInput aria-label="Website URL (e.g., https://example.com)"
                  type="url"
                  placeholder="Website URL (e.g., https://example.com)"
                  value={newSponsor.website_url}
                  onChange={(e) => setNewSponsor({ ...newSponsor, website_url: e.target.value })}
                  className="mb-2 block w-full border"
                />
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <div>
                    <label className="block text-sm mb-1 text-ui-green">Primary Color</label>
                    <FcInput aria-label="Primary Color"
                      type="color"
                      value={newSponsor.color_primary}
                      onChange={(e) => setNewSponsor({ ...newSponsor, color_primary: e.target.value })}
                      className="w-full border"
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-1 text-ui-green">Secondary Color</label>
                    <FcInput aria-label="Secondary Color"
                      type="color"
                      value={newSponsor.color_secondary}
                      onChange={(e) => setNewSponsor({ ...newSponsor, color_secondary: e.target.value })}
                      className="w-full border"
                    />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-ui-green mb-3">
                  <FcInput aria-label="Active"
                    type="checkbox"
                    checked={newSponsor.active}
                    onChange={(e) => setNewSponsor({ ...newSponsor, active: e.target.checked })}
                  />
                  Active
                </label>
                <FcButton variant="primary" type="submit"
                  onClick={handleCreateSponsor}
                  disabled={uploadingLogo}
                  className="disabled:cursor-not-allowed"
                >
                  {uploadingLogo ? 'Uploading Logo...' : 'Create Sponsor'}
                </FcButton>
              </div>
            )}

            {/* Edit Sponsor Form */}
            {editingSponsor && (
              <div className="mb-6 p-4 border rounded bg-surface-canvas">
                <h4 className="font-semibold mb-3 text-ui-green">Edit Sponsor</h4>
                <FcInput aria-label="Sponsor Name *"
                  type="text"
                  placeholder="Sponsor Name *"
                  value={editingSponsor.name}
                  onChange={(e) => setEditingSponsor({ ...editingSponsor, name: e.target.value })}
                  className="mb-2 block w-full border"
                />
                
                {/* Logo Upload Section */}
                <FcCard as="div" padding="standard" className="mb-3 p-3 border rounded bg-surface-card">
                  <label className="block text-sm font-medium mb-2 text-ui-green">Sponsor Logo</label>
                  
                  {editingSponsor.logo_url && !logoPreview && (
                    <div className="mb-2">
                      <p className="text-xs text-content-secondary mb-1">Current logo:</p>
                      <img src={editingSponsor.logo_url} alt="Current logo" className="h-20 object-contain border rounded p-2" onError={(e) => e.target.style.display = 'none'} />
                    </div>
                  )}
                  
                  {!useUrlInput ? (
                    <>
                      <FcInput aria-label="Sponsor Logo"
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                        onChange={handleLogoFileChange}
                        className="mb-2 block w-full file:mr-4 file:border-0"
                      />
                      <p className="text-xs text-content-secondary mb-2">
                        Upload new logo (Max 2MB • PNG, JPG, WebP, SVG • 200-1000px)
                      </p>
                      {logoPreview && (
                        <div className="mb-2">
                          <p className="text-xs text-ui-success mb-1">New logo preview:</p>
                          <img src={logoPreview} alt="New logo preview" className="h-24 object-contain border rounded p-2" />
                        </div>
                      )}
                      <FcButton variant="tertiary"
                        type="button"
                        onClick={() => setUseUrlInput(true)}
                        className="hover:underline"
                      >
                        Or update URL instead
                      </FcButton>
                    </>
                  ) : (
                    <>
                      <FcInput aria-label="Logo URL"
                        type="url"
                        placeholder="Logo URL"
                        value={editingSponsor.logo_url}
                        onChange={(e) => setEditingSponsor({ ...editingSponsor, logo_url: e.target.value })}
                        className="mb-2 block w-full border"
                      />
                      <p className="text-xs text-orange-600 mb-2">
                        ⚠️ External URLs may have security implications. Upload is recommended.
                      </p>
                      <FcButton variant="tertiary"
                        type="button"
                        onClick={() => { setUseUrlInput(false); setEditingSponsor({ ...editingSponsor, logo_url: editingSponsor.logo_url }); }}
                        className="hover:underline"
                      >
                        Upload file instead
                      </FcButton>
                    </>
                  )}
                </FcCard>
                <FcInput aria-label="Website URL"
                  type="url"
                  placeholder="Website URL"
                  value={editingSponsor.website_url}
                  onChange={(e) => setEditingSponsor({ ...editingSponsor, website_url: e.target.value })}
                  className="mb-2 block w-full border"
                />
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <div>
                    <label className="block text-sm mb-1 text-ui-green">Primary Color</label>
                    <FcInput aria-label="Primary Color"
                      type="color"
                      value={editingSponsor.color_primary}
                      onChange={(e) => setEditingSponsor({ ...editingSponsor, color_primary: e.target.value })}
                      className="w-full border"
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-1 text-ui-green">Secondary Color</label>
                    <FcInput aria-label="Secondary Color"
                      type="color"
                      value={editingSponsor.color_secondary}
                      onChange={(e) => setEditingSponsor({ ...editingSponsor, color_secondary: e.target.value })}
                      className="w-full border"
                    />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-ui-green mb-3">
                  <FcInput aria-label="Active"
                    type="checkbox"
                    checked={editingSponsor.active}
                    onChange={(e) => setEditingSponsor({ ...editingSponsor, active: e.target.checked })}
                  />
                  Active
                </label>
                <div className="flex gap-2">
                  <FcButton variant="primary" type="submit"
                    onClick={handleUpdateSponsor}
                    disabled={uploadingLogo}
                    className="disabled:cursor-not-allowed"
                  >
                    {uploadingLogo ? 'Uploading Logo...' : 'Update Sponsor'}
                  </FcButton>
                  <FcButton variant="tertiary" type="submit"
                    onClick={() => {
                      setEditingSponsor(null);
                      setLogoFile(null);
                      setLogoPreview("");
                      setUseUrlInput(false);
                    }}
                    disabled={uploadingLogo}
                    className="disabled:cursor-not-allowed"
                  >
                    Cancel
                  </FcButton>
                </div>
              </div>
            )}

            {/* Sponsors List */}
            <div className="space-y-3">
              {sponsors.length === 0 ? (
                <p className="text-content-secondary text-center py-4">No sponsors found. Create your first one!</p>
              ) : (
                sponsors.map((sponsor) => (
                  <div key={sponsor.id} className="border p-4 rounded bg-gray-50">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-2">
                      <div className="flex-1">
                        <h4 className="font-semibold text-ui-green text-lg">{sponsor.name}</h4>
                        {sponsor.logo_url && (
                          <img src={sponsor.logo_url} alt={sponsor.name} className="h-12 mt-2 object-contain" onError={(e) => e.target.style.display = 'none'} />
                        )}
                        {sponsor.website_url && (
                          <a href={sponsor.website_url} target="_blank" rel="noopener noreferrer" className="text-sm text-ui-green hover:underline">
                            {sponsor.website_url}
                          </a>
                        )}
                        <div className="flex gap-2 mt-2">
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-content-secondary">Primary:</span>
                            <div className="w-6 h-6 rounded border" style={{ backgroundColor: sponsor.color_primary }}></div>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-content-secondary">Secondary:</span>
                            <div className="w-6 h-6 rounded border" style={{ backgroundColor: sponsor.color_secondary }}></div>
                          </div>
                        </div>
                        <p className="text-sm text-content-secondary mt-1">
                          Status: <span className={sponsor.active ? "text-ui-success" : "text-ui-error"}>
                            {sponsor.active ? 'Active' : 'Inactive'}
                          </span>
                        </p>
                      </div>
                      <div className="flex gap-2 mt-2 md:mt-0">
                        <FcButton aria-pressed={editingSponsor === sponsor} variant="primary" type="submit"
                          onClick={() => setEditingSponsor(sponsor)}
                          className=""
                        >
                          Edit
                        </FcButton>
                        <FcButton variant="primary" type="submit"
                          onClick={() => toggleSponsorStatus(sponsor.id, sponsor.active)}
                          className={`${
                            sponsor.active 
                              ? ""
                              : ""
                          }`}
                        >
                          {sponsor.active ? 'Deactivate' : 'Activate'}
                        </FcButton>
                        <FcButton variant="destructive" type="submit"
                          onClick={() => handleDeleteSponsor(sponsor.id)}
                          className=""
                        >
                          Delete
                        </FcButton>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </FcCard>

          {/* 6. Export Sponsor Data Section */}
          <FcCard as="div" padding="standard" className="mb-8 border p-4 rounded shadow-sm bg-surface-card">
            <h3 className="font-semibold mb-2 text-action-primary">Export Sponsor Data</h3>
            <p className="text-sm text-content-secondary mb-4">
              Download questionnaire responses from users who entered sponsored competitions.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-ui-green">Select Competition</label>
                <FcSelect aria-label="Select Competition"
                  value={selectedExportCompetition}
                  onChange={(e) => setSelectedExportCompetition(e.target.value)}
                  className="block w-full border"
                >
                  <option value="">-- Select a sponsored competition --</option>
                  {sponsoredCompetitions.map((comp) => (
                    <option key={comp.id} value={comp.id}>
                      {comp.name} ({comp.league} {comp.season})
                    </option>
                  ))}
                </FcSelect>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                {selectedExportCompetition && (
                  <FcButton variant="primary" type="submit"
                    onClick={handleExportSponsorData}
                    disabled={exportingData}
                    className="w-full sm:w-auto"
                  >
                    {exportingData ? 'Exporting...' : 'Export to CSV'}
                  </FcButton>
                )}

                {exportMessage && (
                  <p className={`text-sm ${exportMessage.type === 'success' ? "text-ui-success" : "text-ui-error"}`}>
                    {exportMessage.text}
                  </p>
                )}
              </div>
            </div>
          </FcCard>

          {/* 7. Sponsor Questions Management Section */}
          <FcCard as="div" padding="standard" className="mb-8 border p-4 rounded shadow-sm bg-surface-card">
            <h3 className="font-semibold mb-4 text-action-primary">Sponsor Questions Management</h3>
            
            {/* Competition Selector */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2 text-ui-green">Select Sponsored Competition</label>
              <FcSelect aria-label="Select Sponsored Competition"
                value={selectedCompetitionForQuestions}
                onChange={(e) => {
                  setSelectedCompetitionForQuestions(e.target.value);
                  setShowQuestionForm(false);
                  setEditingQuestion(null);
                }}
                className="block w-full border"
              >
                <option value="">-- Select a competition --</option>
                {sponsoredCompetitions.map((comp) => (
                  <option key={comp.id} value={comp.id}>
                    {comp.name} ({comp.league} {comp.season}) - {comp.sponsors?.name || 'No sponsor'}
                  </option>
                ))}
              </FcSelect>
              {sponsoredCompetitions.length === 0 && (
                <p className="text-sm text-content-secondary mt-2">No sponsored Scout competitions found. Mark a competition as sponsored first.</p>
              )}
            </div>

            {selectedCompetitionForQuestions && (
              <>
                <div className="flex justify-between items-center mb-4">
                  <p className="text-sm text-content-secondary">
                    Questions: {sponsorQuestions.length} / 3 (max)
                  </p>
                  <FcButton variant="tertiary" type="submit"
                    onClick={() => setShowQuestionForm(!showQuestionForm)}
                    disabled={sponsorQuestions.length >= 3}
                    className="disabled:cursor-not-allowed"
                  >
                    {showQuestionForm ? 'Cancel' : 'Add Question'}
                  </FcButton>
                </div>

                {/* New Question Form */}
                {showQuestionForm && (
                  <div className="mb-6 p-4 border rounded bg-gray-50">
                    <h4 className="font-semibold mb-3 text-ui-green">Create New Question</h4>
                    
                    <label className="block text-sm font-medium mb-1 text-ui-green">Question Type</label>
                    <FcSelect aria-label="Question Type"
                      value={newQuestion.type}
                      onChange={(e) => setNewQuestion({ ...newQuestion, type: e.target.value, options: [] })}
                      className="mb-3 block w-full border"
                    >
                      <option value="short_text">Short Text</option>
                      <option value="long_text">Long Text</option>
                      <option value="radio">Radio Buttons (Single Choice)</option>
                      <option value="dropdown">Dropdown (Single Choice)</option>
                      <option value="multi">Checkboxes (Multiple Choice)</option>
                    </FcSelect>

                    <label className="block text-sm font-medium mb-1 text-ui-green">Question Label *</label>
                    <FcInput aria-label="Question"
                      type="text"
                      placeholder="e.g., What is your favorite product?"
                      value={newQuestion.label}
                      onChange={(e) => setNewQuestion({ ...newQuestion, label: e.target.value })}
                      className="mb-3 block w-full border"
                    />

                    {/* Options for MCQ types */}
                    {['radio', 'dropdown', 'multi'].includes(newQuestion.type) && (
                      <div className="mb-3">
                        <label className="block text-sm font-medium mb-1 text-ui-green">Options</label>
                        <div className="flex gap-2 mb-2">
                          <FcInput label="Enter an option" aria-label="Enter an option"
                            type="text"
                            placeholder="Enter an option"
                            value={optionInput}
                            onChange={(e) => setOptionInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addOption())}
                            className="flex-1 border"
                          />
                          <FcButton variant="tertiary"
                            type="button"
                            onClick={addOption}
                            className=""
                          >
                            Add
                          </FcButton>
                        </div>
                        {newQuestion.options.length > 0 && (
                          <div className="space-y-1">
                            {newQuestion.options.map((option, index) => (
                              <FcCard as="div" padding="standard" key={index} className="flex items-center gap-2 p-2 bg-surface-card border rounded">
                                <span className="flex-1 text-sm text-ui-green">{option}</span>
                                <FcButton variant="destructive"
                                  type="button"
                                  onClick={() => removeOption(index)}
                                  className=""
                                >
                                  Remove
                                </FcButton>
                              </FcCard>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    <label className="flex items-center gap-2 text-ui-green mb-3">
                      <FcInput aria-label="Required"
                        type="checkbox"
                        checked={newQuestion.required}
                        onChange={(e) => setNewQuestion({ ...newQuestion, required: e.target.checked })}
                      />
                      Required
                    </label>

                    <FcButton variant="primary" type="submit"
                      onClick={handleCreateQuestion}
                      className=""
                    >
                      Create Question
                    </FcButton>
                  </div>
                )}

                {/* Edit Question Form */}
                {editingQuestion && (
                  <div className="mb-6 p-4 border rounded bg-surface-canvas">
                    <h4 className="font-semibold mb-3 text-ui-green">Edit Question</h4>
                    
                    <label className="block text-sm font-medium mb-1 text-ui-green">Question Type</label>
                    <FcSelect aria-label="Question Type"
                      value={editingQuestion.type}
                      onChange={(e) => setEditingQuestion({ ...editingQuestion, type: e.target.value, options: [] })}
                      className="mb-3 block w-full border"
                    >
                      <option value="short_text">Short Text</option>
                      <option value="long_text">Long Text</option>
                      <option value="radio">Radio Buttons (Single Choice)</option>
                      <option value="dropdown">Dropdown (Single Choice)</option>
                      <option value="multi">Checkboxes (Multiple Choice)</option>
                    </FcSelect>

                    <label className="block text-sm font-medium mb-1 text-ui-green">Question Label *</label>
                    <FcInput aria-label="Question"
                      type="text"
                      placeholder="e.g., What is your favorite product?"
                      value={editingQuestion.label}
                      onChange={(e) => setEditingQuestion({ ...editingQuestion, label: e.target.value })}
                      className="mb-3 block w-full border"
                    />

                    {/* Options for MCQ types */}
                    {['radio', 'dropdown', 'multi'].includes(editingQuestion.type) && (
                      <div className="mb-3">
                        <label className="block text-sm font-medium mb-1 text-ui-green">Options</label>
                        <div className="flex gap-2 mb-2">
                          <FcInput label="Enter an option" aria-label="Enter an option"
                            type="text"
                            placeholder="Enter an option"
                            value={optionInput}
                            onChange={(e) => setOptionInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addOption())}
                            className="flex-1 border"
                          />
                          <FcButton variant="tertiary"
                            type="button"
                            onClick={addOption}
                            className=""
                          >
                            Add
                          </FcButton>
                        </div>
                        {editingQuestion.options.length > 0 && (
                          <div className="space-y-1">
                            {editingQuestion.options.map((option, index) => (
                              <FcCard as="div" padding="standard" key={index} className="flex items-center gap-2 p-2 bg-surface-card border rounded">
                                <span className="flex-1 text-sm text-ui-green">{option}</span>
                                <FcButton variant="destructive"
                                  type="button"
                                  onClick={() => removeOption(index)}
                                  className=""
                                >
                                  Remove
                                </FcButton>
                              </FcCard>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    <label className="flex items-center gap-2 text-ui-green mb-3">
                      <FcInput aria-label="Required"
                        type="checkbox"
                        checked={editingQuestion.required}
                        onChange={(e) => setEditingQuestion({ ...editingQuestion, required: e.target.checked })}
                      />
                      Required
                    </label>

                    <div className="flex gap-2">
                      <FcButton variant="primary" type="submit"
                        onClick={handleUpdateQuestion}
                        className=""
                      >
                        Update Question
                      </FcButton>
                      <FcButton variant="tertiary" type="submit"
                        onClick={() => {
                          setEditingQuestion(null);
                          setOptionInput("");
                        }}
                        className=""
                      >
                        Cancel
                      </FcButton>
                    </div>
                  </div>
                )}

                {/* Questions List */}
                <div className="space-y-3">
                  {sponsorQuestions.length === 0 ? (
                    <p className="text-content-secondary text-center py-4">No questions for this competition yet. Create your first one!</p>
                  ) : (
                    sponsorQuestions.map((question, index) => (
                      <div key={question.id} className="border p-4 rounded bg-gray-50">
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-2">
                          <div className="flex-1">
                            <h4 className="font-semibold text-ui-green">
                              Question {index + 1}: {question.label}
                            </h4>
                            <p className="text-sm text-content-secondary">
                              Type: <span className="font-medium">{question.type.replace('_', ' ')}</span> | 
                              Required: <span className={question.required ? "text-ui-success" : "text-ui-error"}>
                                {question.required ? 'Yes' : 'No'}
                              </span>
                            </p>
                            {question.options && question.options.length > 0 && (
                              <div className="mt-2">
                                <p className="text-xs text-content-secondary">Options:</p>
                                <ul className="list-disc list-inside text-sm text-content-primary">
                                  {question.options.map((opt, i) => (
                                    <li key={i}>{opt}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                          <div className="flex gap-2 mt-2 md:mt-0">
                            <FcButton variant="primary" type="submit"
                              onClick={() => {
                                setEditingQuestion(question);
                                setShowQuestionForm(false);
                              }}
                              className=""
                            >
                              Edit
                            </FcButton>
                            <FcButton variant="destructive" type="submit"
                              onClick={() => handleDeleteQuestion(question.id)}
                              className=""
                            >
                              Delete
                            </FcButton>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </FcCard>

            </>
          )}

          {activeAdminTab === "users" && (
            <FcCard as="div" padding="standard" className="mb-8 border p-4 rounded shadow-sm bg-surface-card">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
                <h3 className="font-semibold text-action-primary">Users Management</h3>
                <FcButton variant="tertiary" type="submit"
                  onClick={loadUsersTabData}
                  disabled={usersLoading}
                  className="w-full md:w-auto"
                >
                  {usersLoading ? "Refreshing..." : "Refresh Users"}
                </FcButton>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
                <div className="p-3 rounded border bg-gray-50">
                  <div className="text-xs text-content-secondary">Auth Sign-Ups</div>
                  <div className="text-2xl font-bold text-ui-green">{usersSummary.authSignups}</div>
                </div>
                <div className="p-3 rounded border bg-gray-50">
                  <div className="text-xs text-content-secondary">Profiles Created</div>
                  <div className="text-2xl font-bold text-ui-green">{usersSummary.totalUsers}</div>
                </div>
                <div className="p-3 rounded border bg-gray-50">
                  <div className="text-xs text-content-secondary">New Users (Range)</div>
                  <div className="text-2xl font-bold text-ui-green">{usersSummary.newUsersInRange}</div>
                </div>
                <div className="p-3 rounded border bg-gray-50">
                  <div className="text-xs text-content-secondary">Admin Users</div>
                  <div className="text-2xl font-bold text-ui-green">{usersSummary.adminUsers}</div>
                </div>
                <div className="p-3 rounded border bg-gray-50">
                  <div className="text-xs text-content-secondary">Missing Profiles</div>
                  <div className="text-2xl font-bold text-action-primary">{usersSummary.missingProfiles}</div>
                </div>
              </div>

              <FcInput label="Search by username, email, or role" aria-label="Search by username, email, or role"
                type="text"
                value={usersSearch}
                onChange={(e) => setUsersSearch(e.target.value)}
                placeholder="Search by username, email, or role"
                className="mb-4 block w-full border"
              />

              <div className="md:hidden space-y-2 mb-3">
                {usersList
                  .filter((userRow) => {
                    if (!usersSearch.trim()) return true;
                    const query = usersSearch.toLowerCase();
                    return [
                      userRow.username,
                      userRow.email,
                      userRow.role,
                      userRow.time_zone,
                    ]
                      .filter(Boolean)
                      .some((field) => String(field).toLowerCase().includes(query));
                  })
                  .slice(0, 40)
                  .map((userRow) => (
                    <div key={userRow.id} className="border rounded p-3 bg-gray-50">
                      <div className="font-semibold text-ui-green">{userRow.username || "-"}</div>
                      <div className="text-xs text-content-primary break-all">{userRow.email || "-"}</div>
                      <div className="text-xs text-content-secondary mt-1">Role: {userRow.role || "user"}</div>
                      <div className="text-xs text-content-secondary">Time Zone: {userRow.time_zone || "-"}</div>
                      <div className="text-xs text-content-secondary">Joined: {formatShortDate(userRow.created_at)}</div>
                    </div>
                  ))}
              </div>

              <div className="hidden md:block overflow-x-auto border rounded">
                <FcTable label="Admin Fixtures table 1" className="w-full text-sm">
                  <thead className="bg-gray-100 text-ui-green">
                    <tr>
                      <th scope="col" className="text-left p-2">Username</th>
                      <th scope="col" className="text-left p-2">Email</th>
                      <th scope="col" className="text-left p-2">Role</th>
                      <th scope="col" className="text-left p-2">Time Zone</th>
                      <th scope="col" className="text-left p-2">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usersList
                      .filter((userRow) => {
                        if (!usersSearch.trim()) return true;
                        const query = usersSearch.toLowerCase();
                        return [
                          userRow.username,
                          userRow.email,
                          userRow.role,
                          userRow.time_zone,
                        ]
                          .filter(Boolean)
                          .some((field) => String(field).toLowerCase().includes(query));
                      })
                      .slice(0, 100)
                      .map((userRow) => (
                        <tr key={userRow.id} className="border-t">
                          <td className="p-2">{userRow.username || "-"}</td>
                          <td className="p-2">{userRow.email || "-"}</td>
                          <td className="p-2">{userRow.role || "user"}</td>
                          <td className="p-2">{userRow.time_zone || "-"}</td>
                          <td className="p-2">{formatShortDate(userRow.created_at)}</td>
                        </tr>
                      ))}
                  </tbody>
                </FcTable>
              </div>
              <p className="text-xs text-content-secondary mt-2">Showing up to 100 users. Use search to narrow results.</p>

              <div className="mt-6">
                <h4 className="font-semibold text-ui-green mb-2">Users by Time Zone</h4>

                <div className="md:hidden space-y-2">
                  {usersByTimeZone.length === 0 && (
                    <div className="border rounded p-3 text-sm text-content-secondary bg-gray-50">
                      No time zone data available.
                    </div>
                  )}
                  {usersByTimeZone.slice(0, 20).map((row) => (
                    <div key={`tz-mobile-${row.timeZone}`} className="border rounded p-3 bg-gray-50 text-sm">
                      <div className="font-medium text-ui-green break-all">{row.timeZone}</div>
                      <div className="text-content-primary">Users: {row.count}</div>
                    </div>
                  ))}
                </div>

                <div className="hidden md:block overflow-x-auto border rounded">
                  <FcTable label="Users by Time Zone table 2" className="w-full text-sm">
                    <thead className="bg-gray-100 text-ui-green">
                      <tr>
                        <th scope="col" className="text-left p-2">Time Zone</th>
                        <th scope="col" className="text-left p-2">Users</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usersByTimeZone.length === 0 && (
                        <tr>
                          <td className="p-2 text-content-secondary" colSpan={2}>No time zone data available.</td>
                        </tr>
                      )}
                      {usersByTimeZone.map((row) => (
                        <tr key={`tz-${row.timeZone}`} className="border-t">
                          <td className="p-2 break-all">{row.timeZone}</td>
                          <td className="p-2">{row.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </FcTable>
                </div>
                <p className="text-xs text-content-secondary mt-2">Top 20 shown on mobile, full list on desktop.</p>
              </div>
            </FcCard>
          )}

          {activeAdminTab === "analytics" && (
            <FcCard as="div" padding="standard" className="mb-8 border p-4 rounded shadow-sm bg-surface-card">
              <div className="flex flex-col md:flex-row md:items-end gap-3 mb-4">
                <div className="flex-1">
                  <h3 className="font-semibold text-action-primary mb-2">Analytics Dashboard (Phase 1)</h3>
                  <p className="text-xs text-content-secondary">Based on `ADMIN_ANALYTICS_SPRINT_PLAN` metrics and definitions.</p>
                  <p className="text-xs text-content-secondary mt-1">
                    Data source: {analyticsDataSource === "cached_views" ? "Cached analytics views" : "Raw table fallback"}
                  </p>
                </div>
                <FcButton variant="tertiary" type="submit"
                  onClick={loadAnalyticsTabData}
                  disabled={analyticsLoading}
                  className="w-full md:w-auto"
                >
                  {analyticsLoading ? "Loading..." : "Refresh Analytics"}
                </FcButton>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                <div>
                  <label className="block text-xs mb-1 text-ui-green">Date Range</label>
                  <FcSelect aria-label="Date Range"
                    value={analyticsRangePreset}
                    onChange={(e) => setAnalyticsRangePreset(e.target.value)}
                    className="block w-full border"
                  >
                    <option value="today">Today</option>
                    <option value="last_7_days">Last 7 days</option>
                    <option value="last_30_days">Last 30 days</option>
                    <option value="this_month">This month</option>
                    <option value="this_year">This year</option>
                    <option value="custom">Custom range</option>
                  </FcSelect>
                </div>
                <div>
                  <label className="block text-xs mb-1 text-ui-green">Start</label>
                  <FcInput aria-label="Start"
                    type="date"
                    value={analyticsDateRange.startDate}
                    onChange={(e) => {
                      setAnalyticsRangePreset("custom");
                      setAnalyticsDateRange((prev) => ({ ...prev, startDate: e.target.value }));
                    }}
                    className="block w-full border"
                  />
                </div>
                <div>
                  <label className="block text-xs mb-1 text-ui-green">End</label>
                  <FcInput aria-label="End"
                    type="date"
                    value={analyticsDateRange.endDate}
                    onChange={(e) => {
                      setAnalyticsRangePreset("custom");
                      setAnalyticsDateRange((prev) => ({ ...prev, endDate: e.target.value }));
                    }}
                    className="block w-full border"
                  />
                </div>
                <div className="text-xs text-content-secondary flex items-end">
                  Last updated: {analyticsLastUpdated ? new Date(analyticsLastUpdated).toLocaleString("en-GB") : "-"}
                </div>
              </div>

              {analyticsError && (
                <div className="mb-4 p-2 rounded bg-yellow-50 border border-yellow-200 text-yellow-800 text-sm">
                  {analyticsError}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                <div className="p-3 rounded border bg-gray-50">
                  <div className="text-xs text-content-secondary">Weekly Active Selectors (WAS)</div>
                  <div className="text-xl sm:text-2xl font-bold text-ui-green">{analyticsData.weeklyActiveSelectors}</div>
                </div>
                <div className="p-3 rounded border bg-gray-50">
                  <div className="text-xs text-content-secondary">Total Selections</div>
                  <div className="text-xl sm:text-2xl font-bold text-ui-green">{analyticsData.totalSelections}</div>
                </div>
                <div className="p-3 rounded border bg-gray-50">
                  <div className="text-xs text-content-secondary">Unique Selectors</div>
                  <div className="text-xl sm:text-2xl font-bold text-ui-green">{analyticsData.uniqueSelectors}</div>
                </div>
                <div className="p-3 rounded border bg-gray-50">
                  <div className="text-xs text-content-secondary">Avg Selections / Selector</div>
                  <div className="text-xl sm:text-2xl font-bold text-ui-green">{analyticsData.avgSelectionsPerSelector}</div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="border rounded p-3 bg-gray-50">
                  <h4 className="font-semibold text-ui-green mb-2">User Overview</h4>
                  <div className="text-sm text-content-primary">Total users: {analyticsData.totalUsers}</div>
                  <div className="text-sm text-content-primary">New sign-ups (range): {analyticsData.newUsers}</div>
                  <div className="text-sm text-content-primary">DAU: {analyticsData.activeUsersDAU}</div>
                  <div className="text-sm text-content-primary">WAU: {analyticsData.activeUsersWAU}</div>
                  <div className="text-sm text-content-primary">MAU: {analyticsData.activeUsersMAU}</div>
                </div>
                <div className="border rounded p-3 bg-gray-50">
                  <h4 className="font-semibold text-ui-green mb-2">Competition Health</h4>
                  <div className="text-sm text-content-primary">Total competitions: {analyticsData.totalCompetitions}</div>
                  <div className="text-sm text-content-primary">Active competitions: {analyticsData.activeCompetitions}</div>
                  <div className="text-sm text-content-primary">Completed in range: {analyticsData.completedCompetitionsInRange}</div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="border rounded p-3 bg-gray-50">
                  <h4 className="font-semibold text-ui-green mb-2">Retention & Behaviour</h4>
                  <div className="text-sm text-content-primary">Returning selectors (4-week): {analyticsData.returningSelectors4Week}</div>
                  <div className="text-sm text-content-primary">First-time selectors: {analyticsData.firstTimeSelectors}</div>
                  <div className="text-sm text-content-primary">Dormant users (30-day): {analyticsData.dormantUsers30Day}</div>
                </div>
                <div className="border rounded p-3 bg-gray-50">
                  <h4 className="font-semibold text-ui-green mb-2">Referral & Sponsor</h4>
                  <div className="text-sm text-content-primary">Sponsored entries: {analyticsData.sponsoredEntries}</div>
                  <div className="text-sm text-content-primary">
                    Questionnaire completion rate: {analyticsData.sponsorQuestionnaireCompletionRate}%
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h4 className="font-semibold text-ui-green mb-2">Weekly Active Selectors Trend</h4>
                <div className="overflow-x-auto border rounded">
                  <FcTable label="Weekly Active Selectors Trend table 3" className="w-full min-w-[360px] text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th scope="col" className="text-left p-2">Week Start</th>
                        <th scope="col" className="text-left p-2">Active Selectors</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analyticsData.weeklyTrend.length === 0 && (
                        <tr>
                          <td className="p-2" colSpan={2}>No data for selected range.</td>
                        </tr>
                      )}
                      {analyticsData.weeklyTrend.map((row) => (
                        <tr key={row.weekStart} className="border-t">
                          <td className="p-2">{formatShortDate(row.weekStart)}</td>
                          <td className="p-2">{row.weeklyActiveSelectors}</td>
                        </tr>
                      ))}
                    </tbody>
                  </FcTable>
                </div>
              </div>

              {/* Match Day Analytics temporarily hidden */}

              <div className="mb-6">
                <h4 className="font-semibold text-ui-green mb-2">Competition Analytics (Current Match Week)</h4>
                <p className="text-xs text-content-secondary mb-3">
                  Team pick distribution is shown as a percentage of selections made for the selected active competition&apos;s current match day. Submission rate is calculated as submitted players divided by active players.
                </p>

                {analyticsData.competitionCurrentWeekRows.length > 0 && (
                  <div className="mb-3">
                    <label className="block text-xs mb-1 text-ui-green">Select Active Competition</label>
                    <FcSelect aria-label="Select Active Competition"
                      value={selectedAnalyticsCompetitionId}
                      onChange={(e) => setSelectedAnalyticsCompetitionId(e.target.value)}
                      className="block w-full border"
                    >
                      {analyticsData.competitionCurrentWeekRows.map((row) => (
                        <option key={row.competitionId} value={row.competitionId}>
                          {row.competitionName} ({row.league}) - MD {row.currentMatchDay}
                        </option>
                      ))}
                    </FcSelect>
                  </div>
                )}

                {analyticsData.competitionCurrentWeekRows.length === 0 && (
                  <div className="border rounded p-3 text-sm text-content-secondary bg-gray-50">
                    No active competition current-week analytics available.
                  </div>
                )}

                {selectedCompetitionAnalyticsRow && (
                  <div className="border rounded p-3 bg-gray-50">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-2">
                      <div>
                        <div className="font-semibold text-ui-green">{selectedCompetitionAnalyticsRow.competitionName}</div>
                        <div className="text-xs text-content-secondary">{selectedCompetitionAnalyticsRow.league} - Match Day {selectedCompetitionAnalyticsRow.currentMatchDay}</div>
                      </div>
                      <div className="hidden md:block text-xs text-content-primary">
                        Registered: <strong>{selectedCompetitionAnalyticsRow.registeredUsers}</strong> | Active Players: <strong>{selectedCompetitionAnalyticsRow.activeUsers}</strong> | Submitted: <strong>{selectedCompetitionAnalyticsRow.selectionsMade}</strong> | Submission Rate: <strong>{selectedCompetitionAnalyticsRow.submissionRate}%</strong>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mb-3 md:hidden">
                      <FcCard as="div" padding="standard" className="rounded border bg-surface-card p-2">
                        <div className="text-xs text-content-secondary">Registered</div>
                        <div className="text-sm font-semibold text-ui-green">{selectedCompetitionAnalyticsRow.registeredUsers}</div>
                      </FcCard>
                      <FcCard as="div" padding="standard" className="rounded border bg-surface-card p-2">
                        <div className="text-xs text-content-secondary">Active Players</div>
                        <div className="text-sm font-semibold text-ui-green">{selectedCompetitionAnalyticsRow.activeUsers}</div>
                      </FcCard>
                      <FcCard as="div" padding="standard" className="rounded border bg-surface-card p-2">
                        <div className="text-xs text-content-secondary">Submitted</div>
                        <div className="text-sm font-semibold text-ui-green">{selectedCompetitionAnalyticsRow.selectionsMade}</div>
                      </FcCard>
                      <FcCard as="div" padding="standard" className="rounded border bg-surface-card p-2">
                        <div className="text-xs text-content-secondary">Submission Rate</div>
                        <div className="text-sm font-semibold text-ui-green">{selectedCompetitionAnalyticsRow.submissionRate}%</div>
                      </FcCard>
                    </div>

                    <div className="space-y-2">
                      {selectedCompetitionAnalyticsRow.teamBreakdown.length === 0 && (
                        <FcCard as="div" padding="standard" className="p-3 rounded border bg-surface-card text-sm text-content-secondary">
                          No selections submitted yet for this match day.
                        </FcCard>
                      )}

                      {(() => {
                        const maxCount = Math.max(
                          ...selectedCompetitionAnalyticsRow.teamBreakdown.map((row) => Number(row.count || 0)),
                          1
                        );

                        return selectedCompetitionAnalyticsRow.teamBreakdown.map((teamRow) => {
                          const barWidth = Number(teamRow.count || 0) > 0
                            ? Number(((Number(teamRow.count || 0) / maxCount) * 100).toFixed(2))
                            : 0;

                          return (
                            <FcCard as="div" padding="standard"
                              key={`${selectedCompetitionAnalyticsRow.competitionId}-${teamRow.team}`}
                              className="rounded border bg-surface-card p-2"
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-32 md:w-44 shrink-0 text-sm font-medium text-ui-green truncate" title={teamRow.team}>
                                  {teamRow.team}
                                </div>
                                <div className="flex-1">
                                  <div className="h-4 bg-gray-200 rounded overflow-hidden">
                                    <div
                                      className="h-4 bg-action-primary"
                                      style={{ width: `${barWidth}%` }}
                                    />
                                  </div>
                                </div>
                                <div className="w-28 shrink-0 text-right text-xs text-content-primary whitespace-nowrap">
                                  {teamRow.count} picks ({teamRow.percentage}%)
                                </div>
                              </div>
                            </FcCard>
                          );
                        });
                      })()}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <h4 className="font-semibold text-ui-green mb-2">League Engagement</h4>
                <div className="md:hidden space-y-2">
                  {analyticsData.leagueRows.length === 0 && (
                    <div className="border rounded p-3 text-sm text-content-secondary bg-gray-50">No league data for selected range.</div>
                  )}
                  {analyticsData.leagueRows.map((row) => (
                    <div key={`mobile-${row.league}`} className="border rounded p-3 bg-gray-50 text-sm">
                      <div className="font-semibold text-ui-green">{row.league}</div>
                      <div className="text-content-primary">Unique Selectors: {row.uniqueSelectors}</div>
                      <div className="text-content-primary">Total Selections: {row.totalSelections}</div>
                      <div className="text-content-primary">Avg / Selector: {row.avgSelectionsPerSelector}</div>
                      <div className="text-content-primary">Active Competitions: {row.activeCompetitions}</div>
                      <div className="text-content-primary">Engagement Score: {row.engagementScore}</div>
                    </div>
                  ))}
                </div>
                <div className="hidden md:block overflow-x-auto border rounded">
                  <FcTable label="League Engagement table 4" className="w-full min-w-[780px] text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th scope="col" className="text-left p-2">League</th>
                        <th scope="col" className="text-left p-2">Unique Selectors</th>
                        <th scope="col" className="text-left p-2">Total Selections</th>
                        <th scope="col" className="text-left p-2">Avg / Selector</th>
                        <th scope="col" className="text-left p-2">Active Competitions</th>
                        <th scope="col" className="text-left p-2">Engagement Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analyticsData.leagueRows.length === 0 && (
                        <tr>
                          <td className="p-2" colSpan={6}>No league data for selected range.</td>
                        </tr>
                      )}
                      {analyticsData.leagueRows.map((row) => (
                        <tr key={row.league} className="border-t">
                          <td className="p-2">{row.league}</td>
                          <td className="p-2">{row.uniqueSelectors}</td>
                          <td className="p-2">{row.totalSelections}</td>
                          <td className="p-2">{row.avgSelectionsPerSelector}</td>
                          <td className="p-2">{row.activeCompetitions}</td>
                          <td className="p-2">{row.engagementScore}</td>
                        </tr>
                      ))}
                    </tbody>
                  </FcTable>
                </div>
              </div>
            </FcCard>
          )}



        {activeAdminTab === "support" && (
        <FcCard as="div" padding="standard" className="border border-ui-border rounded p-6 bg-surface-card mb-6">
          <div>
            <h3 className="font-semibold mb-4 text-action-primary">Support Tickets Management</h3>
            
            {/* Filter Tabs */}
            <div className="flex flex-wrap gap-2 mb-6">
              {[
                { value: "all", label: "All" },
                { value: "new", label: "New" },
                { value: "in_progress", label: "In Progress" },
                { value: "resolved", label: "Resolved" },
                { value: "closed", label: "Closed" },
              ].map((filter) => {
                const count = filter.value === "all" 
                  ? supportTickets.length 
                  : supportTickets.filter(t => t.status === filter.value).length;
                
                return (
                  <FcButton variant="primary" type="submit"
                    key={filter.value}
                    onClick={() => setTicketFilter(filter.value)}
                    className={`${
                      ticketFilter === filter.value
                        ? ""
                        : ""
                    }`}
                  >
                    {filter.label} ({count})
                  </FcButton>
                );
              })}
            </div>

            {/* Tickets List */}
            <div className="space-y-4">
              {supportTickets
                .filter(ticket => ticketFilter === "all" || ticket.status === ticketFilter)
                .map((ticket) => {
                  const isExpanded = selectedTicket === ticket.id;
                  
                  const getStatusColor = (status) => {
                    const colors = {
                      new: "bg-blue-100 text-blue-800",
                      in_progress: "bg-yellow-100 text-yellow-800",
                      resolved: "bg-green-100 text-green-800",
                      closed: "bg-gray-100 text-gray-800",
                    };
                    return colors[status] || "bg-gray-100 text-gray-800";
                  };

                  const getPriorityColor = (priority) => {
                    const colors = {
                      low: "bg-gray-100 text-gray-700",
                      medium: "bg-blue-100 text-blue-700",
                      high: "bg-orange-100 text-orange-700",
                      urgent: "bg-red-100 text-red-700",
                    };
                    return colors[priority] || "bg-gray-100 text-gray-700";
                  };

                  const getCategoryIcon = (category) => {
                    const icons = {
                      bug: "🐛",
                      feature: "💡",
                      question: "❓",
                      account: "👤",
                      other: "📝",
                    };
                    return icons[category] || "📝";
                  };

                  return (
                    <div key={ticket.id} className="border rounded-lg overflow-hidden">
                      {/* Ticket Header */}
                      <FcButton variant="secondary" type="button" aria-expanded={isExpanded} aria-label={`Ticket: ${ticket.subject}`}
                        className="fc-ticket-disclosure w-full text-left"
                        onClick={() => {
                          setSelectedTicket(isExpanded ? null : ticket.id);
                          setAdminNotes(ticket.admin_notes || '');
                        }}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <span className="text-2xl">{getCategoryIcon(ticket.category)}</span>
                              <h4 className="font-semibold text-ui-green">{ticket.subject}</h4>
                            </div>
                            <div className="flex flex-wrap gap-2 mb-2">
                              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(ticket.status)}`}>
                                {ticket.status.replace('_', ' ').toUpperCase()}
                              </span>
                              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(ticket.priority)}`}>
                                {ticket.priority.toUpperCase()}
                              </span>
                              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-content-primary">
                                {ticket.category.toUpperCase()}
                              </span>
                            </div>
                            <p className="text-sm text-content-secondary">
                              <strong>From:</strong> {ticket.username} ({ticket.email})<br />
                              <strong>Created:</strong> {new Date(ticket.created_at).toLocaleString('en-GB')}
                              {ticket.updated_at !== ticket.created_at && (
                                <> • <strong>Updated:</strong> {new Date(ticket.updated_at).toLocaleString('en-GB')}</>
                              )}
                            </p>
                          </div>
                          <svg
                            className={`w-6 h-6 text-ui-green transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </FcButton>

                      {/* Expanded Details */}
                      {isExpanded && (
                        <div className="p-4 border-t bg-surface-card">
                          {/* Browser Info */}
                          {ticket.browser_info && (
                            <div className="mb-4">
                              <h5 className="text-sm font-semibold text-ui-green mb-2">Technical Info:</h5>
                              <div className="text-xs text-content-secondary bg-gray-50 p-3 rounded space-y-1">
                                <p><strong>User Agent:</strong> {ticket.browser_info.userAgent}</p>
                                <p><strong>Platform:</strong> {ticket.browser_info.platform}</p>
                                <p><strong>Screen:</strong> {ticket.browser_info.screenResolution}</p>
                                <p><strong>Viewport:</strong> {ticket.browser_info.viewport}</p>
                                {ticket.page_url && <p><strong>Page URL:</strong> {ticket.page_url}</p>}
                              </div>
                            </div>
                          )}

                          {/* Conversation */}
                          <div className="mb-4">
                            <h5 className="text-sm font-semibold text-ui-green mb-4">💬 Conversation:</h5>
                            <TicketConversation 
                              ticketId={ticket.id} 
                              userId={session.user.id}
                              isAdmin={true}
                              initialMessage={ticket.message}
                              createdAt={ticket.created_at}
                            />
                          </div>

                          {/* Status Update */}
                          <div className="flex flex-wrap gap-2 pt-4 border-t">
                            <FcButton variant="primary" type="submit"
                              onClick={() => handleUpdateTicketStatus(ticket.id, 'new')}
                              disabled={updatingTicket || ticket.status === 'new'}
                              className="disabled:cursor-not-allowed"
                            >
                              Mark as New
                            </FcButton>
                            <FcButton variant="tertiary" type="submit"
                              onClick={() => handleUpdateTicketStatus(ticket.id, 'in_progress')}
                              disabled={updatingTicket || ticket.status === 'in_progress'}
                              className="disabled:cursor-not-allowed"
                            >
                              Mark as In Progress
                            </FcButton>
                            <FcButton variant="primary" type="submit"
                              onClick={() => handleUpdateTicketStatus(ticket.id, 'resolved')}
                              disabled={updatingTicket || ticket.status === 'resolved'}
                              className="disabled:cursor-not-allowed"
                            >
                              Mark as Resolved
                            </FcButton>
                            <FcButton variant="tertiary" type="submit"
                              onClick={() => handleUpdateTicketStatus(ticket.id, 'closed')}
                              disabled={updatingTicket || ticket.status === 'closed'}
                              className="disabled:cursor-not-allowed"
                            >
                              Close Ticket
                            </FcButton>
                          </div>

                          {/* Ticket ID */}
                          <p className="text-xs text-content-secondary mt-4">Ticket ID: {ticket.id}</p>
                        </div>
                      )}
                    </div>
                  );
                })}

              {supportTickets.filter(ticket => ticketFilter === "all" || ticket.status === ticketFilter).length === 0 && (
                <div className="text-center py-12 text-content-secondary">
                  <p className="text-4xl mb-2">🎫</p>
                  <p>No {ticketFilter !== "all" ? ticketFilter.replace('_', ' ') : ''} tickets found</p>
                </div>
              )}
            </div>
          </div>
        </FcCard>
        )}

        {activeAdminTab === "legal" && (
          <>
          {/* 9. Legal Documents Management Section */}
          <FcCard as="div" padding="standard" className="mb-8 border p-4 rounded shadow-sm bg-surface-card">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2">
              <h3 className="font-semibold text-action-primary">Legal Documents Management</h3>
              <FcButton variant="tertiary" type="submit"
                onClick={() => setShowLegalDocForm(!showLegalDocForm)}
                className=""
              >
                {showLegalDocForm ? 'Cancel' : 'Create New Document'}
              </FcButton>
            </div>

            {/* Filter by document type */}
            <div className="mb-4 flex flex-wrap gap-2">
              {['all', 'terms', 'privacy', 'cookies'].map((type) => (
                <FcButton aria-pressed={legalDocFilter === type} variant="tertiary" type="submit"
                  key={type}
                  onClick={() => setLegalDocFilter(type)}
                  className={`${
                    legalDocFilter === type
                      ? ""
                      : ""
                  }`}
                >
                  {type === 'all' ? 'All Documents' : type.charAt(0).toUpperCase() + type.slice(1)}
                </FcButton>
              ))}
            </div>

            {/* New Legal Document Form */}
            {showLegalDocForm && (
              <div className="mb-6 p-4 border rounded bg-gray-50">
                <h4 className="font-semibold mb-3 text-ui-green">Create New Legal Document</h4>
                
                <label className="block text-sm font-medium mb-1 text-ui-green">Document Type *</label>
                <FcSelect aria-label="Document Type *"
                  value={newLegalDoc.type}
                  onChange={(e) => setNewLegalDoc({ ...newLegalDoc, type: e.target.value })}
                  className="mb-3 block w-full border"
                >
                  <option value="terms">Terms of Service</option>
                  <option value="privacy">Privacy Policy</option>
                  <option value="cookies">Cookie Policy</option>
                </FcSelect>

                <label className="block text-sm font-medium mb-1 text-ui-green">Version * (e.g., v1.0, v2.0)</label>
                <FcInput aria-label="Version (e.g., v1.0)"
                  type="text"
                  placeholder="Version (e.g., v1.0)"
                  value={newLegalDoc.version}
                  onChange={(e) => setNewLegalDoc({ ...newLegalDoc, version: e.target.value })}
                  className="mb-3 block w-full border"
                />

                <label className="block text-sm font-medium mb-1 text-ui-green">Title *</label>
                <FcInput aria-label="Document Title"
                  type="text"
                  placeholder="Document Title"
                  value={newLegalDoc.title}
                  onChange={(e) => setNewLegalDoc({ ...newLegalDoc, title: e.target.value })}
                  className="mb-3 block w-full border"
                />

                <label className="block text-sm font-medium mb-1 text-ui-green">Content * (HTML supported)</label>
                <FcTextarea aria-label="Document content (HTML supported)"
                  placeholder="Document content (HTML supported)"
                  value={newLegalDoc.content}
                  onChange={(e) => setNewLegalDoc({ ...newLegalDoc, content: e.target.value })}
                  rows="10"
                  className="mb-3 block w-full border resize-vertical"
                />

                <div className="flex flex-col md:flex-row gap-2 mb-3">
                  <div className="flex-1">
                    <label className="block text-sm font-medium mb-1 text-ui-green">Effective Date *</label>
                    <FcInput aria-label="Effective Date *"
                      type="date"
                      value={newLegalDoc.effective_date}
                      onChange={(e) => setNewLegalDoc({ ...newLegalDoc, effective_date: e.target.value })}
                      className="border w-full"
                    />
                  </div>
                  <label className="flex items-center gap-2 text-ui-green self-end">
                    <FcInput aria-label="Make Active (users must accept)"
                      type="checkbox"
                      checked={newLegalDoc.is_active}
                      onChange={(e) => setNewLegalDoc({ ...newLegalDoc, is_active: e.target.checked })}
                    />
                    Make Active (users must accept)
                  </label>
                </div>

                <FcButton variant="primary" type="submit"
                  onClick={handleCreateLegalDoc}
                  className=""
                >
                  Create Legal Document
                </FcButton>
              </div>
            )}

            {/* Edit Legal Document Form */}
            {editingLegalDoc && (
              <div className="mb-6 p-4 border rounded bg-surface-canvas">
                <h4 className="font-semibold mb-3 text-ui-green">Edit Legal Document</h4>
                
                <label className="block text-sm font-medium mb-1 text-ui-green">Document Type *</label>
                <FcSelect aria-label="Document Type *"
                  value={editingLegalDoc.type}
                  onChange={(e) => setEditingLegalDoc({ ...editingLegalDoc, type: e.target.value })}
                  className="mb-3 block w-full border"
                >
                  <option value="terms">Terms of Service</option>
                  <option value="privacy">Privacy Policy</option>
                  <option value="cookies">Cookie Policy</option>
                </FcSelect>

                <label className="block text-sm font-medium mb-1 text-ui-green">Version *</label>
                <FcInput aria-label="Version (e.g., v1.0)"
                  type="text"
                  placeholder="Version (e.g., v1.0)"
                  value={editingLegalDoc.version}
                  onChange={(e) => setEditingLegalDoc({ ...editingLegalDoc, version: e.target.value })}
                  className="mb-3 block w-full border"
                />

                <label className="block text-sm font-medium mb-1 text-ui-green">Title *</label>
                <FcInput aria-label="Document Title"
                  type="text"
                  placeholder="Document Title"
                  value={editingLegalDoc.title}
                  onChange={(e) => setEditingLegalDoc({ ...editingLegalDoc, title: e.target.value })}
                  className="mb-3 block w-full border"
                />

                <label className="block text-sm font-medium mb-1 text-ui-green">Content * (HTML supported)</label>
                <FcTextarea aria-label="Document content (HTML supported)"
                  placeholder="Document content (HTML supported)"
                  value={editingLegalDoc.content}
                  onChange={(e) => setEditingLegalDoc({ ...editingLegalDoc, content: e.target.value })}
                  rows="10"
                  className="mb-3 block w-full border resize-vertical"
                />

                <div className="flex flex-col md:flex-row gap-2 mb-3">
                  <div className="flex-1">
                    <label className="block text-sm font-medium mb-1 text-ui-green">Effective Date *</label>
                    <FcInput aria-label="Effective Date *"
                      type="date"
                      value={editingLegalDoc.effective_date}
                      onChange={(e) => setEditingLegalDoc({ ...editingLegalDoc, effective_date: e.target.value })}
                      className="border w-full"
                    />
                  </div>
                  <label className="flex items-center gap-2 text-ui-green self-end">
                    <FcInput aria-label="Active"
                      type="checkbox"
                      checked={editingLegalDoc.is_active}
                      onChange={(e) => setEditingLegalDoc({ ...editingLegalDoc, is_active: e.target.checked })}
                    />
                    Active
                  </label>
                </div>

                <div className="flex gap-2">
                  <FcButton variant="primary" type="submit"
                    onClick={handleUpdateLegalDoc}
                    className=""
                  >
                    Update Document
                  </FcButton>
                  <FcButton variant="tertiary" type="submit"
                    onClick={() => setEditingLegalDoc(null)}
                    className=""
                  >
                    Cancel
                  </FcButton>
                </div>
              </div>
            )}

            {/* Legal Documents List */}
            <div className="space-y-3">
              {legalDocuments.length === 0 ? (
                <p className="text-content-secondary text-center py-4">No legal documents found. Create your first one!</p>
              ) : (
                legalDocuments
                  .filter(doc => legalDocFilter === 'all' || doc.type === legalDocFilter)
                  .map((doc) => (
                    <div key={doc.id} className="border p-4 rounded bg-gray-50">
                      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-2">
                        <div>
                          <h4 className="font-semibold text-ui-green">
                            {doc.type.toUpperCase()} - {doc.version}: {doc.title}
                          </h4>
                          <p className="text-sm text-content-secondary">
                            Effective: {new Date(doc.effective_date).toLocaleDateString('en-GB')} | 
                            Status: <span className={doc.is_active ? "text-ui-success font-semibold" : "text-ui-error"}>
                              {doc.is_active ? '✓ ACTIVE (users must accept)' : 'Inactive'}
                            </span>
                          </p>
                          <p className="text-xs text-content-secondary mt-1">
                            Created: {new Date(doc.created_at).toLocaleDateString('en-GB')}
                            {doc.updated_at !== doc.created_at && ` • Updated: ${new Date(doc.updated_at).toLocaleDateString('en-GB')}`}
                          </p>
                        </div>
                        <div className="flex gap-2 mt-2 md:mt-0">
                          <FcButton aria-pressed={editingLegalDoc === doc} variant="primary" type="submit"
                            onClick={() => setEditingLegalDoc(doc)}
                            className=""
                          >
                            Edit
                          </FcButton>
                          <FcButton variant="primary" type="submit"
                            onClick={() => toggleLegalDocActiveStatus(doc.id, doc.is_active)}
                            className={`${
                              doc.is_active 
                                ? ""
                                : ""
                            }`}
                          >
                            {doc.is_active ? 'Deactivate' : 'Activate'}
                          </FcButton>
                          <FcButton variant="destructive" type="submit"
                            onClick={() => handleDeleteLegalDoc(doc.id)}
                            className=""
                          >
                            Delete
                          </FcButton>
                        </div>
                      </div>
                      <FcCard as="div" padding="standard" className="text-sm text-content-primary bg-surface-card p-3 rounded border mt-2 max-h-40 overflow-y-auto">
                        <div
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(doc.content || "", { USE_PROFILES: { html: true } })
                          }}
                        />
                      </FcCard>
                    </div>
                  ))
              )}
            </div>
          </FcCard>
          </>
        )}
      </FcTabs>
</div>

      {/* Bottom navbar for mobile */}
      <BottomNavbar userRole={userRole} handleLogout={async () => { await supabase.auth.signOut(); navigate("/login"); }} />
    </div>
  );
}
